import React, { useEffect, useMemo, useState } from 'react';
import { createRoot } from 'react-dom/client';
import { initializeApp, type FirebaseApp } from 'firebase/app';
import {
  browserLocalPersistence,
  getAuth,
  getRedirectResult,
  GoogleAuthProvider,
  onAuthStateChanged,
  setPersistence,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  type User,
} from 'firebase/auth';
import shopkeeperHero from './assets/shopkeeper-hero-v2.webp';
import { LegalPage, type LegalPageName } from './LegalPages';
import { TrainingPage } from './TrainingPage';
import { MarketingCounters, MarketingExtras } from './MarketingExtras';
import { AgentConnectionEvidence, AgentTypeBadge } from './AgentConnectionEvidence';
import { WorkflowCartoon } from './WorkflowCartoon';
import { Icon } from './PortalIcon';
import { PrinterConnectionOptions, type PrinterConnectionPlatform } from './PrinterConnectionOptions';
import { AdminChatButton, AdminChatInbox, SupportChatWidget } from './SupportChat';
import { CountryPhoneInput, countryName, possibleContactPhone, type CountryCode } from './CountryPhoneInput';
import './styles.css';

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('/sw.js').catch(() => undefined);
  });
}

type SubscriptionStatus = 'Active' | 'Renewal due' | 'Suspended' | 'Setup required';
type PinPaymentMode = 'demo_pin' | 'counter_pin';
type PaymentAccess = {
  checkoutMode: 'manual' | 'razorpay' | PinPaymentMode | 'setup_required';
  razorpayConnected: boolean;
  pinSession: {
    active: boolean;
    id: string;
    mode: PinPaymentMode | '';
    pin: string;
    startedAt: string;
    expiresAt: string;
  };
};
type ShopProfile = {
  shopId?: string;
  shopName?: string;
  email?: string;
  subscriptionStatus?: SubscriptionStatus;
  onboardingRequired?: boolean;
  onboardingComplete?: boolean;
  termsAccepted?: boolean;
  printingPaused?: boolean;
  printingPausedAt?: string;
  city?: string;
  phoneNumber?: string;
  address?: string;
  pinCode?: string;
  openingTime?: string;
  closingTime?: string;
  paperSizes?: string[];
  printModes?: string[];
  paymentProvider?: string;
  paymentConfigured?: boolean;
  paymentConnectionMode?: 'oauth' | 'legacy_keys' | 'platform' | 'manual';
  paymentConnectionStatus?: 'connected' | 'reconnect_required' | 'platform_available' | 'manual_required';
  razorpayEnvironment?: 'test' | 'live' | '';
  razorpayOAuthApplicationCurrent?: boolean;
  razorpayReconnectRequired?: boolean;
  paymentManualFallback?: boolean;
  printing?: { jobs: number; value: number };
  razorpayAccountId?: string;
  checkoutMode?: PaymentAccess['checkoutMode'];
  paymentAvailable?: boolean;
  manualPaymentEnabled?: boolean;
  paymentAccess?: PaymentAccess;
  rates?: {
    bw: number;
    color: number;
    byPaper?: Record<'A4' | 'Legal', { bw: number; color: number }>;
  };
  subscription?: {
    planCode: string;
    planName: string;
    monthlyAmount: number;
    status: SubscriptionStatus;
    validUntil: string;
    testMode: boolean;
  };
  createdAt?: string;
  agentFirstSeenAt?: string;
  agentConnectionVerifiedAt?: string;
  agentLastSeenAt?: string;
  agentVersion?: string;
  agentPlatform?: 'android' | 'windows' | '';
  agentInstallerDownloadRequestedAt?: string;
  agentPortableDownloadRequestedAt?: string;
  agentAndroidDownloadRequestedAt?: string;
  agentDownloadLastRequestedAt?: string;
  agentDownloadLastKind?: 'installer' | 'portable_zip' | 'android_apk' | '';
  agentDownloadRequestCount?: number;
  agentPaymentMode?: 'manual' | 'gateway' | '';
  agentPaymentProvider?: string;
  referralAgent?: { code: string; name: string; lockedAt: string } | null;
  supportContact?: { type: 'sales_agent' | 'call_centre'; name: string; phoneNumber: string; availableUntil: string; message: string };
  isTestAccount?: boolean;
  capabilities?: { admin: boolean; tester: boolean; testControlsEnabled: boolean };
};
type PortalOrder = {
  orderId: string;
  shopId: string;
  shopName?: string;
  fileName: string;
  amount: number;
  pages: number;
  pageRange?: string;
  copies: number;
  mode: string;
  status: string;
  createdAt: string;
};
type OrderRetention = {
  terminalMetadataMinutes: number;
  cleanupIntervalHours: number;
  documentMaximumHours: number;
};
type AuditEvent = {
  id: string;
  actorEmail: string;
  action: string;
  targetType: string;
  targetId: string;
  createdAt: string;
};
type AdminOverview = {
  shops: number;
  customers: number;
  orders: number;
  shopCounts: Record<string, number>;
  orderCounts: Record<string, number>;
  shopsLast7Days: number;
  customersLast7Days: number;
  ordersLast24Hours: number;
  ordersLast7Days: number;
  completedLast7Days: number;
  failedLast7Days: number;
  failedToday: number;
  printedPagesToday: number;
  activeJobs: number;
  activeAgents: number;
  subscriptionsDueSoon: number;
  completedValue: number;
  testShops: number;
  testCustomers: number;
  generatedAt: string;
};
type AdminUser = {
  uid: string;
  email: string;
  displayName: string;
  source: 'firebase-claim' | 'configuration';
  disabled: boolean;
  creationTime: string;
  lastSignInTime: string;
};
type SupportRequest = {
  id: string;
  source: 'shopkeeper' | 'public';
  shopId: string;
  shopName: string;
  shopEmail: string;
  phoneNumber: string;
  country?: string;
  callingCode?: string;
  type: 'query' | 'suggestion';
  message: string;
  createdAt: string;
  expiresAt: string;
};
type SalesAgent = { code: string; name: string; phoneNumber: string; city: string; email: string; active: boolean; createdAt?: string };
type AdminCustomer = { uid: string; email: string; displayName: string; createdAt: string; lastLoginAt: string; isShopkeeper: boolean; isTestAccount: boolean };
type AdminSection = 'shops' | 'customers' | 'orders' | 'audit' | 'users' | 'salesAgents' | 'supportRequests' | 'chats';
type AdminSectionState = 'idle' | 'loading' | 'loaded' | 'error';
type ShopView = 'overview' | 'payments' | 'profile' | 'agent' | 'support' | 'orders';
type AgentDownloadKind = 'installer' | 'portable_zip' | 'android_apk';

const adminSectionDetails: Record<AdminSection, { title: string; description: string }> = {
  users: { title: 'Administrator access', description: 'Explore and manage authorized platform accounts.' },
  shops: { title: 'Shopkeepers & test accounts', description: 'Classify test shopkeepers so their activity stays out of production reporting.' },
  customers: { title: 'Customers & testers', description: 'See customer accounts separately from tester accounts.' },
  salesAgents: { title: 'Sales agents', description: 'Explore, edit, and share onboarding-agent details.' },
  chats: { title: 'Live chat inbox', description: 'Reply to website visitors and shopkeepers. Live support: 10 AM–6 PM IST, every day.' },
  supportRequests: { title: 'Support inbox', description: 'Shopkeeper queries and suggestions. Requests are automatically deleted after 30 days.' },
  orders: { title: 'Platform orders', description: 'Explore the latest order records without leaving the dashboard.' },
  audit: { title: 'Admin audit trail', description: 'Explore recent administrative actions.' }
};

function getInitialShopView(): ShopView {
  const requestedView = window.location.hash.replace('#', '');
  if (requestedView === 'subscription') return 'agent';
  return ['overview', 'payments', 'profile', 'agent', 'support', 'orders'].includes(requestedView)
    ? requestedView as ShopView
    : 'overview';
}

const agentInstallerUrl =
  import.meta.env.VITE_AGENT_INSTALLER_URL ||
  import.meta.env.NEXT_PUBLIC_AGENT_INSTALLER_URL ||
  'https://localagentinstaller.blob.core.windows.net/localagentreleases/SaralPrint-LocalAgent-Setup.exe';

const agentZipUrl =
  import.meta.env.VITE_AGENT_ZIP_URL ||
  import.meta.env.NEXT_PUBLIC_AGENT_ZIP_URL ||
  'https://localagentinstaller.blob.core.windows.net/localagentreleases/SaralPrint-LocalAgent-win-x64.zip';

const androidAgentApkUrl =
  import.meta.env.VITE_ANDROID_AGENT_APK_URL ||
  import.meta.env.NEXT_PUBLIC_ANDROID_AGENT_APK_URL ||
  'https://localagentinstaller.blob.core.windows.net/localagentreleases/SaralPrint-Agent-Android.apk';

const showShopkeeperSubscription = import.meta.env.VITE_SHOW_SHOPKEEPER_SUBSCRIPTION === 'true';

const cloudApiUrl =
  import.meta.env.VITE_CLOUD_API_URL ||
  import.meta.env.NEXT_PUBLIC_CLOUD_API_URL ||
  'https://saralprint-cloud-api.azurewebsites.net/api';

const razorpayMerchantSignupUrl =
  import.meta.env.VITE_RAZORPAY_MERCHANT_SIGNUP_URL ||
  import.meta.env.NEXT_PUBLIC_RAZORPAY_MERCHANT_SIGNUP_URL ||
  '';

const merchantPortalUrl = (
  import.meta.env.VITE_MERCHANT_PORTAL_URL ||
  import.meta.env.NEXT_PUBLIC_MERCHANT_PORTAL_URL ||
  new URL('/merchant/', window.location.origin).toString()
).replace(/\/$/, '');

const hasRazorpayMerchantSignupUrl = /^https:\/\//i.test(razorpayMerchantSignupUrl);
const adminPortal = import.meta.env.VITE_ADMIN_PORTAL === 'true' ||
  window.location.hostname === 'admin.saralprint.com' ||
  window.location.pathname === '/admin' || window.location.pathname.startsWith('/admin/') ||
  (import.meta.env.DEV && new URLSearchParams(window.location.search).get('admin') === '1');

const legalPageAliases: Record<string, LegalPageName> = {
  '/privacy': 'privacy',
  '/privacy-policy': 'privacy',
  '/terms': 'terms',
  '/terms-of-service': 'terms',
  '/user-agreement': 'terms',
  '/support': 'support',
};

const isTrainingPage = window.location.pathname.toLowerCase().replace(/\/$/, '') === '/training';

function getDefaultSubscriptionStatus(): SubscriptionStatus {
  return 'Setup required';
}

function getSubscriptionStatusMessage(status: SubscriptionStatus) {
  if (status === 'Active') return 'Your free shop access is active.';
  if (status === 'Suspended') return 'This shop is suspended. Contact platform support before accepting new orders.';
  return 'Complete shop setup to activate free access.';
}

const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY || import.meta.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN || import.meta.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID || import.meta.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID || import.meta.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

const hasFirebaseConfig = Object.values(firebaseConfig).every(Boolean);
let firebaseApp: FirebaseApp | null = null;

if (hasFirebaseConfig) {
  firebaseApp = initializeApp(firebaseConfig);
}

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [shopName, setShopName] = useState('Demo Print Shop');
  const [city, setCity] = useState('Pune');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [pinCode, setPinCode] = useState('');
  const [openingTime, setOpeningTime] = useState('09:00');
  const [closingTime, setClosingTime] = useState('18:00');
  const [blackWhiteRate, setBlackWhiteRate] = useState(3);
  const [colorRate, setColorRate] = useState(10);
  const [legalBlackWhiteRate, setLegalBlackWhiteRate] = useState(5);
  const [legalColorRate, setLegalColorRate] = useState(15);
  const [supportsColorPrinting, setSupportsColorPrinting] = useState(false);
  const [supportsLegalPaper, setSupportsLegalPaper] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [referralAgentName, setReferralAgentName] = useState('');
  const [referralLocked, setReferralLocked] = useState(false);
  const [referralMessage, setReferralMessage] = useState('');
  const [supportContact, setSupportContact] = useState<ShopProfile['supportContact']>();
  const [supportPhoneNumber, setSupportPhoneNumber] = useState('');
  const [supportCountry, setSupportCountry] = useState<CountryCode>('IN');
  const [supportRequestType, setSupportRequestType] = useState<'query' | 'suggestion'>('query');
  const [supportRequestMessage, setSupportRequestMessage] = useState('');
  const [supportRequestStatus, setSupportRequestStatus] = useState('');
  const [supportRequestBusy, setSupportRequestBusy] = useState(false);
  const [chatOpenSignal, setChatOpenSignal] = useState(0);
  const [chatDraft, setChatDraft] = useState('');
  function openPrinterSetup(platform: PrinterConnectionPlatform) {
    setShopView('agent');
    window.requestAnimationFrame(() => {
      const target = document.getElementById(`${platform}-print-agent`);
      target?.focus({ preventScroll: true });
      target?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }
  function enquireAboutDevice() {
    setChatDraft('I’d like to buy the SaralPrint device priced at ₹5,000. My printer model is: ');
    setChatOpenSignal(value => value + 1);
  }
  const [connectedAgentPlatform, setConnectedAgentPlatform] = useState<ShopProfile['agentPlatform']>('');
  const [shopSaved, setShopSaved] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [onboardingRequired, setOnboardingRequired] = useState(false);
  const [printingPaused, setPrintingPaused] = useState(false);
  const [printingPauseBusy, setPrintingPauseBusy] = useState(false);
  const [printingPauseMessage, setPrintingPauseMessage] = useState('');
  const [profileMessage, setProfileMessage] = useState('');
  const [subscription, setSubscription] = useState<SubscriptionStatus>(() => getDefaultSubscriptionStatus());
  const [subscriptionMessage, setSubscriptionMessage] = useState(() => getSubscriptionStatusMessage(getDefaultSubscriptionStatus()));
  const [subscriptionPlan, setSubscriptionPlan] = useState('Saral Free');
  const [cloudShopId, setCloudShopId] = useState('');
  const [shopIdCopyStatus, setShopIdCopyStatus] = useState('Copy');
  const [authMessage, setAuthMessage] = useState('');
  const [authBusy, setAuthBusy] = useState(false);
  const [paymentMessage, setPaymentMessage] = useState('');
  const [paymentConfigured, setPaymentConfigured] = useState(false);
  const [paymentConnectionMode, setPaymentConnectionMode] = useState<ShopProfile['paymentConnectionMode']>('manual');
  const [razorpayAccountId, setRazorpayAccountId] = useState('');
  const [paymentBusy, setPaymentBusy] = useState(false);
  const [manualPaymentEnabled, setManualPaymentEnabled] = useState(false);
  const [paymentAccess, setPaymentAccess] = useState<PaymentAccess>({
    checkoutMode: 'setup_required',
    razorpayConnected: false,
    pinSession: { active: false, id: '', mode: '', pin: '', startedAt: '', expiresAt: '' },
  });
  const [pinMode, setPinMode] = useState<PinPaymentMode>('demo_pin');
  const [pinDurationMinutes, setPinDurationMinutes] = useState(120);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isTester, setIsTester] = useState(false);
  const [adminOverview, setAdminOverview] = useState<AdminOverview | null>(null);
  const [adminMessage, setAdminMessage] = useState('');
  const [shopOrders, setShopOrders] = useState<PortalOrder[]>([]);
  const [now, setNow] = useState(() => Date.now());
  const [testControlsAvailable, setTestControlsAvailable] = useState(false);
  const [orderMessage, setOrderMessage] = useState('');
  const [adminShops, setAdminShops] = useState<ShopProfile[]>([]);
  const [adminIdentityDeletionConfigured, setAdminIdentityDeletionConfigured] = useState(false);
  const [adminShopFilter, setAdminShopFilter] = useState<'production' | 'incomplete' | 'test' | 'all'>('production');
  const [adminCustomers, setAdminCustomers] = useState<AdminCustomer[]>([]);
  const [adminOrders, setAdminOrders] = useState<PortalOrder[]>([]);
  const [adminOrderTotalCount, setAdminOrderTotalCount] = useState(0);
  const [adminOrderRetention, setAdminOrderRetention] = useState<OrderRetention | null>(null);
  const [adminAudit, setAdminAudit] = useState<AuditEvent[]>([]);
  const [adminSupportRequests, setAdminSupportRequests] = useState<SupportRequest[]>([]);
  const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
  const [salesAgents, setSalesAgents] = useState<SalesAgent[]>([]);
  const [salesAgentName, setSalesAgentName] = useState('');
  const [salesAgentPhone, setSalesAgentPhone] = useState('');
  const [salesAgentCity, setSalesAgentCity] = useState('');
  const [salesAgentEmail, setSalesAgentEmail] = useState('');
  const [editingSalesAgentCode, setEditingSalesAgentCode] = useState('');
  const [salesAgentMessage, setSalesAgentMessage] = useState('');
  const [adminEmail, setAdminEmail] = useState('');
  const [adminUserMessage, setAdminUserMessage] = useState('');
  const [adminUserBusy, setAdminUserBusy] = useState(false);
  const [adminManagementConfigured, setAdminManagementConfigured] = useState(false);
  const [adminSections, setAdminSections] = useState<Record<AdminSection, AdminSectionState>>({ shops: 'idle', customers: 'idle', orders: 'idle', audit: 'idle', users: 'idle', salesAgents: 'idle', supportRequests: 'idle', chats: 'idle' });
  const [adminSectionErrors, setAdminSectionErrors] = useState<Partial<Record<AdminSection, string>>>({});
  const [activeAdminSection, setActiveAdminSection] = useState<AdminSection | null>(null);
  const [adminShareMessage, setAdminShareMessage] = useState('');
  const [adminAccess, setAdminAccess] = useState<'checking' | 'granted' | 'denied'>(adminPortal ? 'checking' : 'denied');
  const [showAdmin, setShowAdmin] = useState(adminPortal);
  const [shopView, setShopView] = useState<ShopView>(() => getInitialShopView());

  const signedIn = Boolean(user);
  const displayName = user?.displayName || 'Shopkeeper';
  const firstName = displayName.trim().split(/\s+/)[0] || 'Shopkeeper';
  const authError = formatAuthError(authMessage);
  const canDownloadAgent = Boolean(cloudShopId && shopSaved && (!onboardingRequired || termsAccepted));
  const canActivate = shopSaved && (!onboardingRequired || termsAccepted) && subscription === 'Active';

  useEffect(() => {
    if (!firebaseApp) {
      setAuthMessage('Firebase login is not configured for this portal build.');
      return undefined;
    }

    const auth = getAuth(firebaseApp);
    void setPersistence(auth, browserLocalPersistence).catch(() => undefined);

    void getRedirectResult(auth)
      .then((result) => {
        if (result?.user) {
          setUser(result.user);
        }
      })
      .catch((error) => {
        const message = error instanceof Error ? error.message : 'Google sign-in failed.';
        setAuthMessage(`Google sign-in failed. Check Firebase Google provider and authorized domains. ${message}`);
      })
      .finally(() => setAuthBusy(false));

    return onAuthStateChanged(auth, (nextUser) => {
      setUser(nextUser);
      if (nextUser) {
        setAuthMessage('');
      }
    });
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const razorpay = params.get('razorpay');
    if (razorpay === 'connected') {
      setPaymentConfigured(true);
      setPaymentConnectionMode('oauth');
      setPaymentMessage('Razorpay connected. Cloud will create payment links and confirm payments from Razorpay webhooks.');
    } else if (razorpay === 'failed') {
      setPaymentMessage(`Razorpay connection failed: ${params.get('reason') || 'authorization was not completed'}.`);
    }

    if (razorpay) {
      window.history.replaceState({}, '', `${window.location.pathname}${window.location.hash || '#payments'}`);
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setCloudShopId('');
      if (adminPortal) setAdminAccess('checking');
      return;
    }

    if (adminPortal) {
      setShowAdmin(true);
      void loadAdminOverview(user);
      return;
    }

    void fetchMyShop(user).catch((error) => {
      const message = cloudErrorMessage(error, 'Shop profile sync');
      setProfileMessage(message);
    });
    void loadMyOrders(user);
  }, [user]);

  useEffect(() => {
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, []);

  useEffect(() => {
    if (!user) {
      setIsAdmin(false);
      setIsTester(false);
      setAdminOverview(null);
      return;
    }
    void user.getIdTokenResult().then((token) => {
      const admin = token.claims.admin === true;
      const tester = admin || token.claims.tester === true;
      setIsAdmin((current) => current || admin);
      if (admin) setAdminAccess('granted');
      setIsTester((current) => current || tester);
      if (admin && showAdmin) void loadAdminOverview(user);
    }).catch(() => setIsAdmin(false));
  }, [user, showAdmin]);

  async function loadMyOrders(activeUser: User) {
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/shops/me/orders`, {
        headers: { authorization: `Bearer ${idToken}` },
      });
      const body = await response.json() as { orders?: PortalOrder[]; testControlsEnabled?: boolean; error?: string };
      if (!response.ok) throw new Error(body.error || 'Shop orders could not be loaded.');
      setShopOrders((body.orders || []).sort((left, right) => right.createdAt.localeCompare(left.createdAt)));
      setTestControlsAvailable(Boolean(body.testControlsEnabled));
      setOrderMessage(`Updated ${new Date().toLocaleTimeString()}`);
    } catch (error) {
      setOrderMessage(error instanceof Error ? error.message : 'Shop orders could not be loaded.');
    }
  }

  async function loadAdminOverview(activeUser: User) {
    try {
      setAdminMessage('Loading compact overview...');
      const idToken = await activeUser.getIdToken();
      const headers = { authorization: `Bearer ${idToken}` };
      const overviewResponse = await fetch(`${cloudApiUrl}/platform-admin/overview`, { headers });
      const overview = await overviewResponse.json() as AdminOverview & { error?: string };
      if (overviewResponse.status === 403) setAdminAccess('denied');
      if (!overviewResponse.ok) throw new Error(overview.error || 'Admin overview failed.');
      setIsAdmin(true);
      setAdminAccess('granted');
      setAdminOverview(overview);
      setAdminMessage(`Updated ${new Date(overview.generatedAt).toLocaleString()}`);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : 'Admin overview failed.');
    }
  }

  async function shareAdminSnapshot() {
    if (!adminOverview) return;
    const resolvedOutcomes = adminOverview.completedLast7Days + adminOverview.failedLast7Days;
    const completionRate = resolvedOutcomes
      ? Math.round((adminOverview.completedLast7Days / resolvedOutcomes) * 100)
      : 0;
    const summary = [
      'SaralPrint KPI Snapshot',
      `Shopkeepers: ${adminOverview.shops} (${adminOverview.shopCounts.Active || 0} active)`,
      `Registered users: ${adminOverview.customers}`,
      `Orders: ${adminOverview.ordersLast24Hours} today · ${adminOverview.ordersLast7Days} in 7 days`,
      `Completed: ${adminOverview.completedLast7Days} (${completionRate}% of resolved 7-day outcomes)`,
      `Active jobs: ${adminOverview.activeJobs} · Agents online: ${adminOverview.activeAgents}`,
      `Access attention: ${adminOverview.shopCounts.Suspended || 0} suspended`,
      `Failed in 7 days: ${adminOverview.failedLast7Days}`,
      `Generated: ${new Date(adminOverview.generatedAt).toLocaleString()}`,
    ].join('\n');
    try {
      if (navigator.share) {
        await navigator.share({ title: 'SaralPrint KPI Snapshot', text: summary });
        setAdminShareMessage('KPI summary shared.');
      } else {
        await navigator.clipboard.writeText(summary);
        setAdminShareMessage('KPI summary copied to clipboard.');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      setAdminShareMessage('Could not share automatically. You can take a screenshot of this KPI card.');
    }
  }

  function setAdminSectionState(section: AdminSection, state: AdminSectionState, error = '') {
    setAdminSections((current) => ({ ...current, [section]: state }));
    setAdminSectionErrors((current) => ({ ...current, [section]: error }));
  }

  function toggleAdminSection(section: AdminSection) {
    const shouldOpen = activeAdminSection !== section;
    setActiveAdminSection(shouldOpen ? section : null);
    if (!shouldOpen || !user) return;
    if (section === 'users') void loadAdminUsers(user);
    if (section === 'shops') void loadAdminShops(user);
    if (section === 'customers') void loadAdminCustomers(user);
    if (section === 'salesAgents') void loadSalesAgents(user);
    if (section === 'orders') void loadAdminOrders(user);
    if (section === 'audit') void loadAdminAudit(user);
    if (section === 'supportRequests') void loadAdminSupportRequests(user);
  }

  useEffect(() => {
    if (!activeAdminSection) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveAdminSection(null);
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [activeAdminSection]);

  async function loadAdminShops(activeUser: User, force = false, filterOverride = adminShopFilter) {
    if (!force && (adminSections.shops === 'loading' || adminSections.shops === 'loaded')) return;
    setAdminSectionState('shops', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const filter = filterOverride === 'test'
        ? 'testAccounts=true'
        : filterOverride === 'production'
          ? 'productionAccounts=true'
          : filterOverride === 'incomplete'
            ? 'onboardingIncomplete=true'
            : '';
      const response = await fetch(`${cloudApiUrl}/platform-admin/shops${filter ? `?${filter}` : ''}`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { shops?: ShopProfile[]; identityDeletionConfigured?: boolean; error?: string };
      if (!response.ok) throw new Error(body.error || 'Shop details could not be loaded.');
      setAdminShops(body.shops || []);
      setAdminIdentityDeletionConfigured(Boolean(body.identityDeletionConfigured));
      setAdminSectionState('shops', 'loaded');
    } catch (error) {
      setAdminSectionState('shops', 'error', error instanceof Error ? error.message : 'Shop details could not be loaded.');
    }
  }

  async function loadAdminOrders(activeUser: User, force = false) {
    if (!force && (adminSections.orders === 'loading' || adminSections.orders === 'loaded')) return;
    setAdminSectionState('orders', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/orders?limit=500`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { orders?: PortalOrder[]; totalCount?: number; retention?: OrderRetention; testControlsEnabled?: boolean; error?: string };
      if (!response.ok) throw new Error(body.error || 'Recent orders could not be loaded.');
      setAdminOrders(body.orders || []);
      setAdminOrderTotalCount(body.totalCount ?? body.orders?.length ?? 0);
      setAdminOrderRetention(body.retention || null);
      setTestControlsAvailable(Boolean(body.testControlsEnabled));
      setAdminSectionState('orders', 'loaded');
    } catch (error) {
      setAdminSectionState('orders', 'error', error instanceof Error ? error.message : 'Recent orders could not be loaded.');
    }
  }

  async function loadAdminAudit(activeUser: User, force = false) {
    if (!force && (adminSections.audit === 'loading' || adminSections.audit === 'loaded')) return;
    setAdminSectionState('audit', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/audit?limit=30`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { events?: AuditEvent[]; error?: string };
      if (!response.ok) throw new Error(body.error || 'Audit history could not be loaded.');
      setAdminAudit(body.events || []);
      setAdminSectionState('audit', 'loaded');
    } catch (error) {
      setAdminSectionState('audit', 'error', error instanceof Error ? error.message : 'Audit history could not be loaded.');
    }
  }

  async function loadAdminCustomers(activeUser: User, force = false) {
    if (!force && (adminSections.customers === 'loading' || adminSections.customers === 'loaded')) return;
    setAdminSectionState('customers', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/customers`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { customers?: AdminCustomer[]; error?: string };
      if (!response.ok) throw new Error(body.error || 'Customer accounts could not be loaded.');
      setAdminCustomers(body.customers || []);
      setAdminSectionState('customers', 'loaded');
    } catch (error) {
      setAdminSectionState('customers', 'error', error instanceof Error ? error.message : 'Customer accounts could not be loaded.');
    }
  }

  async function loadAdminSupportRequests(activeUser: User, force = false) {
    if (!force && (adminSections.supportRequests === 'loading' || adminSections.supportRequests === 'loaded')) return;
    setAdminSectionState('supportRequests', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/support-requests`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { requests?: SupportRequest[]; error?: string };
      if (!response.ok) throw new Error(body.error || 'Support requests could not be loaded.');
      setAdminSupportRequests(body.requests || []);
      setAdminSectionState('supportRequests', 'loaded');
    } catch (error) {
      setAdminSectionState('supportRequests', 'error', error instanceof Error ? error.message : 'Support requests could not be loaded.');
    }
  }

  async function submitShopSupportRequest() {
    if (!user || supportRequestBusy) return;
    try {
      setSupportRequestBusy(true);
      setSupportRequestStatus('Sending your request…');
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/support/requests`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ country: supportCountry, phoneNumber: supportPhoneNumber, type: supportRequestType, message: supportRequestMessage }),
      });
      const body = await response.json() as { requestId?: string; error?: string };
      if (!response.ok) throw new Error(body.error || 'Support request could not be sent.');
      setSupportRequestMessage('');
      setSupportRequestStatus(`Request ${body.requestId || ''} received. Our team will get back to you.`);
    } catch (error) {
      setSupportRequestStatus(error instanceof Error ? error.message : 'Support request could not be sent.');
    } finally {
      setSupportRequestBusy(false);
    }
  }

  async function loadAdminUsers(activeUser: User, force = false) {
    if (!force && (adminSections.users === 'loading' || adminSections.users === 'loaded')) return;
    setAdminSectionState('users', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/admins`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { users?: AdminUser[]; managementConfigured?: boolean; error?: string };
      if (!response.ok) throw new Error(body.error || 'Administrator group could not be loaded.');
      setAdminUsers(body.users || []);
      setAdminManagementConfigured(Boolean(body.managementConfigured));
      setAdminSectionState('users', 'loaded');
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Administrator group could not be loaded.';
      setAdminUserMessage(message);
      setAdminSectionState('users', 'error', message);
    }
  }

  async function grantAdminAccess() {
    if (!user || !adminEmail.trim()) return;
    try {
      setAdminUserBusy(true);
      setAdminUserMessage(`Adding ${adminEmail.trim()}...`);
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/admins`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ email: adminEmail.trim() }),
      });
      const body = await response.json() as { user?: AdminUser; message?: string; error?: string };
      if (!response.ok) throw new Error(body.error || 'Administrator access could not be granted.');
      setAdminEmail('');
      setAdminUserMessage(body.message || 'Administrator access granted.');
      await loadAdminUsers(user, true);
    } catch (error) {
      setAdminUserMessage(error instanceof Error ? error.message : 'Administrator access could not be granted.');
    } finally {
      setAdminUserBusy(false);
    }
  }

  async function markTestOrderPaid(order: PortalOrder) {
    if (!user) return;
    try {
      setOrderMessage(`Approving mock payment for ${order.orderId}...`);
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/test/orders/${encodeURIComponent(order.orderId)}/mock-pay`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ shopId: order.shopId }),
      });
      const body = await response.json() as PortalOrder & { error?: string };
      if (!response.ok) throw new Error(body.error || 'Mock payment failed.');
      setOrderMessage(`Order ${order.orderId} is mock-paid. The local agent can now download and print it.`);
      await Promise.all([
        loadMyOrders(user),
        isAdmin ? loadAdminOverview(user) : Promise.resolve(),
        isAdmin && adminSections.orders === 'loaded' ? loadAdminOrders(user, true) : Promise.resolve(),
      ]);
    } catch (error) {
      setOrderMessage(error instanceof Error ? error.message : 'Mock payment failed.');
    }
  }

  async function updateAdminSubscription(shop: ShopProfile, action: 'suspend' | 'activate') {
    if (!user || !shop.shopId) return;
    try {
      setAdminMessage(`Updating ${shop.shopName || shop.shopId}...`);
      const idToken = await user.getIdToken();
      const payload = { subscriptionStatus: action === 'suspend' ? 'Suspended' : 'Active' };
      const response = await fetch(`${cloudApiUrl}/platform-admin/shops/${encodeURIComponent(shop.shopId)}`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await response.json() as ShopProfile & { error?: string };
      if (!response.ok) throw new Error(body.error || 'Shop update failed.');
      await Promise.all([loadAdminOverview(user), loadAdminShops(user, true)]);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : 'Shop update failed.');
    }
  }

  async function updateAdminPrintingPause(shop: ShopProfile, paused: boolean) {
    if (!user || !shop.shopId) return;
    const shopLabel = shop.shopName || shop.shopId;
    if (paused && !window.confirm(`Pause printing for ${shopLabel}? Customers will not be able to place new orders and the local agent will not start another print job.`)) return;
    try {
      setAdminMessage(`${paused ? 'Pausing' : 'Resuming'} printing for ${shopLabel}...`);
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/shops/${encodeURIComponent(shop.shopId)}`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ printingPaused: paused }),
      });
      const body = await response.json() as ShopProfile & { error?: string };
      if (!response.ok) throw new Error(body.error || 'Shop printing availability could not be updated.');
      await Promise.all([loadAdminOverview(user), loadAdminShops(user, true)]);
      setAdminMessage(`${shopLabel} is now ${paused ? 'paused' : 'accepting print jobs'}.`);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : 'Shop printing availability could not be updated.');
    }
  }

  async function updateTestAccount(shop: ShopProfile, isTestAccount: boolean) {
    if (!user || !shop.shopId) return;
    try {
      setAdminMessage(`${isTestAccount ? 'Marking' : 'Restoring'} ${shop.shopName || shop.shopId}...`);
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/shops/${encodeURIComponent(shop.shopId)}`, {
        method: 'POST', headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ isTestAccount }),
      });
      const body = await response.json() as ShopProfile & { error?: string };
      if (!response.ok) throw new Error(body.error || 'Test-account classification failed.');
      await Promise.all([loadAdminOverview(user), loadAdminShops(user, true)]);
      setAdminMessage(`${shop.shopName || shop.shopId} is now ${isTestAccount ? 'a test account' : 'a production account'}.`);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : 'Test-account classification failed.');
    }
  }

  async function deleteAdminShop(shop: ShopProfile) {
    if (!user || !shop.shopId) return;
    try {
      setAdminMessage(`Deleting ${shop.shopName || shop.shopId}...`);
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/shops/${encodeURIComponent(shop.shopId)}`, {
        method: 'DELETE',
        headers: { authorization: `Bearer ${idToken}` },
      });
      const body = await response.json() as { shopId?: string; identityDeleted?: boolean; identityDeletionStatus?: 'deleted' | 'not_found' | 'not_configured'; ordersDeleted?: number; documentsDeleted?: number; error?: string };
      if (!response.ok) throw new Error(body.error || 'Shop could not be deleted.');
      await Promise.all([loadAdminOverview(user), loadAdminShops(user, true)]);
      const identityMessage = body.identityDeletionStatus === 'deleted'
        ? 'Firebase user access was removed.'
        : body.identityDeletionStatus === 'not_configured'
          ? 'Firebase user access remains because Firebase Admin credentials are not configured.'
          : 'No linked Firebase user remained.';
      setAdminMessage(`${shop.shopName || shop.shopId} deleted. ${identityMessage} Removed ${body.ordersDeleted || 0} order${body.ordersDeleted === 1 ? '' : 's'} and ${body.documentsDeleted || 0} stored document${body.documentsDeleted === 1 ? '' : 's'}.`);
    } catch (error) {
      setAdminMessage(error instanceof Error ? error.message : 'Shop could not be deleted.');
    }
  }

  function applySubscription(profile: ShopProfile) {
    const status = profile.subscription?.status || profile.subscriptionStatus || 'Renewal due';
    setSubscription(status);
    setSubscriptionPlan(profile.subscription?.planName || profile.subscription?.planCode || 'Trial');
    setSubscriptionMessage(getSubscriptionStatusMessage(status));
  }

  async function loginWithGoogle() {
    if (!firebaseApp) {
      setAuthMessage('Firebase login is not configured for this portal build.');
      return;
    }

    try {
      setAuthBusy(true);
      setAuthMessage('');
      const auth = getAuth(firebaseApp);
      await setPersistence(auth, browserLocalPersistence);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      setUser(result.user);
      setAuthMessage('');
      setAuthBusy(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Google sign-in failed.';
      const code = typeof error === 'object' && error && 'code' in error ? String(error.code) : '';
      const popupUnavailable = code === 'auth/popup-blocked' || code === 'auth/cancelled-popup-request';
      if (popupUnavailable && firebaseApp && window.matchMedia('(display-mode: browser)').matches) {
        try {
          const auth = getAuth(firebaseApp);
          const provider = new GoogleAuthProvider();
          provider.setCustomParameters({ prompt: 'select_account' });
          await signInWithRedirect(auth, provider);
          return;
        } catch (redirectError) {
          const redirectMessage = redirectError instanceof Error ? redirectError.message : 'Google sign-in failed.';
          setAuthMessage(`Google sign-in failed. ${redirectMessage}`);
        }
      } else if (popupUnavailable) {
        setAuthMessage('Google sign-in needs a browser window. Allow pop-ups for this site and try again.');
      } else {
        setAuthMessage(`Google sign-in failed. ${message}`);
      }
      setAuthBusy(false);
    }
  }

  async function logout() {
    if (firebaseApp) {
      await signOut(getAuth(firebaseApp)).catch(() => undefined);
    }
    setUser(null);
    setAuthMessage('');
  }

  function checkSubscriptionStatus() {
    if (user) void fetchMyShop(user).catch((error) => {
      setSubscriptionMessage(error instanceof Error ? error.message : 'Subscription refresh failed.');
    });
  }

  async function readCloudError(response: Response, fallback: string) {
    try {
      const body = await response.json() as { error?: string };
      if (body.error) {
        return body.error;
      }
    } catch {
      // Ignore non-JSON error bodies.
    }

    if (response.status === 503) {
      return 'Cloud API is unavailable (503). Restart or redeploy the Azure Function App saralprint-cloud-api, then try again.';
    }

    return fallback;
  }

  function cloudErrorMessage(error: unknown, action: string) {
    if (error instanceof TypeError) {
      return `${action} failed because the portal could not reach the cloud API. Check that the latest cloud-functions deployment is complete and CORS is available at ${cloudApiUrl}.`;
    }

    return error instanceof Error ? error.message : `${action} failed.`;
  }

  async function fetchMyShop(activeUser: User) {
    const idToken = await activeUser.getIdToken();
    const response = await fetch(`${cloudApiUrl}/shops/me`, {
      headers: {
        authorization: `Bearer ${idToken}`,
      },
    });
    if (!response.ok) {
      throw new Error(await readCloudError(response, 'Cloud shop profile could not be loaded.'));
    }

    const profile = await response.json() as ShopProfile;
    if (!profile.shopId) {
      throw new Error('Cloud did not return a shop id. Save shop registration once the API is online.');
    }
    setCloudShopId(profile.shopId);
    if (profile.capabilities) {
      setIsAdmin(profile.capabilities.admin);
      if (profile.capabilities.admin) setAdminAccess('granted');
      setIsTester(profile.capabilities.tester);
      setTestControlsAvailable(profile.capabilities.testControlsEnabled && profile.capabilities.tester);
      if (profile.capabilities.admin) void loadAdminOverview(activeUser);
    }
    if (profile.shopName) {
      setShopName(profile.shopName);
    }
    setCity(profile.city || '');
    setPhoneNumber(profile.phoneNumber || '');
    setAddress(profile.address || '');
    setPinCode(profile.pinCode || '');
    setOpeningTime(profile.openingTime || '09:00');
    setClosingTime(profile.closingTime || '18:00');
    setBlackWhiteRate(profile.rates?.bw || 3);
    setColorRate(profile.rates?.color || 10);
    setLegalBlackWhiteRate(profile.rates?.byPaper?.Legal?.bw || profile.rates?.bw || 5);
    setLegalColorRate(profile.rates?.byPaper?.Legal?.color || profile.rates?.color || 15);
    setSupportsColorPrinting(profile.printModes?.includes('Color') === true);
    setSupportsLegalPaper(profile.paperSizes?.includes('Legal') === true);
    setReferralCode(profile.referralAgent?.code || '');
    setReferralAgentName(profile.referralAgent?.name || '');
    setReferralLocked(Boolean(profile.referralAgent?.code));
    setSupportContact(profile.supportContact);
    setConnectedAgentPlatform(profile.agentPlatform || '');
    setSupportPhoneNumber(profile.phoneNumber || '');
    setShopSaved(Boolean(profile.shopName && profile.city && profile.phoneNumber && profile.address && profile.pinCode));
    setTermsAccepted(Boolean(profile.termsAccepted));
    setOnboardingRequired(profile.onboardingRequired === true);
    setPrintingPaused(profile.printingPaused === true);
    applySubscription(profile);
    setPaymentConfigured(Boolean(profile.paymentConfigured));
    setManualPaymentEnabled(profile.manualPaymentEnabled === true);
    setPaymentConnectionMode(profile.paymentConnectionMode || 'manual');
    setRazorpayAccountId(profile.razorpayAccountId || '');
    if (profile.paymentAccess) {
      setPaymentAccess(profile.paymentAccess);
      if (profile.paymentAccess.pinSession.mode) setPinMode(profile.paymentAccess.pinSession.mode);
    }
    if (profile.paymentConfigured) {
      setPaymentMessage(profile.paymentConnectionMode === 'oauth'
        ? 'Razorpay is connected. Customers can pay online and the cloud will mark paid orders automatically.'
        : 'Razorpay cloud payments are connected using the older key-based setup.');
    }
  }

  async function loadSalesAgents(activeUser: User, force = false) {
    if (!force && (adminSections.salesAgents === 'loading' || adminSections.salesAgents === 'loaded')) return;
    setAdminSectionState('salesAgents', 'loading');
    try {
      const idToken = await activeUser.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/sales-agents`, { headers: { authorization: `Bearer ${idToken}` } });
      const body = await response.json() as { agents?: SalesAgent[]; error?: string };
      if (!response.ok) throw new Error(body.error || 'Sales agents could not be loaded.');
      setSalesAgents(body.agents || []);
      setAdminSectionState('salesAgents', 'loaded');
    } catch (error) {
      setAdminSectionState('salesAgents', 'error', error instanceof Error ? error.message : 'Sales agents could not be loaded.');
    }
  }

  async function createSalesAgent() {
    if (!user) return;
    try {
      setSalesAgentMessage('Creating onboarding agent...');
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/sales-agents`, {
        method: 'POST', headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ name: salesAgentName, phoneNumber: salesAgentPhone, city: salesAgentCity, email: salesAgentEmail }),
      });
      const body = await response.json() as { agent?: SalesAgent; error?: string };
      if (!response.ok || !body.agent) throw new Error(body.error || 'Sales agent could not be created.');
      setSalesAgentMessage(`${body.agent.name} created. Referral code: ${body.agent.code}`);
      setSalesAgentName(''); setSalesAgentPhone(''); setSalesAgentCity(''); setSalesAgentEmail('');
      await loadSalesAgents(user, true);
    } catch (error) {
      setSalesAgentMessage(error instanceof Error ? error.message : 'Sales agent could not be created.');
    }
  }

  async function saveSalesAgent() {
    if (!user || !editingSalesAgentCode) return;
    try {
      setSalesAgentMessage(`Saving ${editingSalesAgentCode}...`);
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/platform-admin/sales-agents/${encodeURIComponent(editingSalesAgentCode)}`, {
        method: 'POST', headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ name: salesAgentName, phoneNumber: salesAgentPhone, city: salesAgentCity, email: salesAgentEmail })
      });
      const body = await response.json() as { agent?: SalesAgent; error?: string };
      if (!response.ok || !body.agent) throw new Error(body.error || 'Sales agent could not be updated.');
      const updatedAgent = body.agent;
      setSalesAgents((current) => current.map((agent) => agent.code === updatedAgent.code ? updatedAgent : agent));
      cancelSalesAgentEdit();
      setSalesAgentMessage(`${editingSalesAgentCode} updated.`);
    } catch (error) {
      setSalesAgentMessage(error instanceof Error ? error.message : 'Sales agent could not be updated.');
    }
  }

  function startSalesAgentEdit(agent: SalesAgent) {
    setEditingSalesAgentCode(agent.code);
    setSalesAgentName(agent.name);
    setSalesAgentPhone(agent.phoneNumber);
    setSalesAgentCity(agent.city);
    setSalesAgentEmail(agent.email || '');
    setSalesAgentMessage(`Editing ${agent.code}`);
  }

  function cancelSalesAgentEdit() {
    setEditingSalesAgentCode('');
    setSalesAgentName(''); setSalesAgentPhone(''); setSalesAgentCity(''); setSalesAgentEmail('');
  }

  async function copySalesAgentCard(agent: SalesAgent) {
    const text = salesAgentShareText(agent);
    try {
      await navigator.clipboard.writeText(text);
      setSalesAgentMessage(`${agent.code} card copied to clipboard.`);
    } catch {
      setSalesAgentMessage('Could not copy the agent card. Use WhatsApp or email share instead.');
    }
  }

  async function verifyReferralCode() {
    if (!user || !referralCode.trim()) return;
    try {
      setReferralMessage('Checking agent code...');
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/sales-agents/lookup?code=${encodeURIComponent(referralCode.trim())}`, {
        headers: { authorization: `Bearer ${idToken}` },
      });
      const body = await response.json() as { agent?: { code: string; name: string }; error?: string };
      if (!response.ok || !body.agent) throw new Error(body.error || 'Agent code was not found.');
      setReferralCode(body.agent.code);
      setReferralAgentName(body.agent.name);
      setReferralMessage(`Onboarding agent: ${body.agent.name}`);
    } catch (error) {
      setReferralAgentName('');
      setReferralMessage(error instanceof Error ? error.message : 'Agent code could not be verified.');
    }
  }

  async function connectRazorpay() {
    setPaymentMessage('Opening Merchant Hub. Razorpay authorization will continue there.');
    window.location.assign(`${merchantPortalUrl}/?portal=saralprint#payments`);
  }

  async function updateManualPayment(enabled: boolean) {
    if (!user) return;
    setPaymentBusy(true);
    setPaymentMessage(enabled ? 'Enabling pay at counter…' : 'Disabling pay at counter…');
    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/agent/manual-payment`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ enabled }),
      });
      const profile = await response.json() as ShopProfile & { error?: string };
      if (!response.ok) throw new Error(profile.error || 'Could not update pay at counter.');
      setManualPaymentEnabled(profile.manualPaymentEnabled === true);
      setPaymentAccess((current) => profile.paymentAccess || { ...current, checkoutMode: profile.checkoutMode || current.checkoutMode });
      setPaymentMessage(enabled
        ? 'Customers can now choose pay at counter. Print jobs wait until you confirm payment in the agent.'
        : 'New customers can no longer choose pay at counter. Review any existing manual orders in the agent.');
    } catch (error) {
      setPaymentMessage(cloudErrorMessage(error, 'Pay at counter'));
    } finally {
      setPaymentBusy(false);
    }
  }

  async function disconnectRazorpay() {
    if (!user) {
      setPaymentMessage('Sign in before disconnecting Razorpay.');
      return;
    }

    try {
      setPaymentBusy(true);
      setPaymentMessage('Disconnecting Razorpay...');
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/shops/me/razorpay/oauth/disconnect`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
        },
      });
      const profile = await response.json() as ShopProfile & { error?: string };
      if (!response.ok) {
        throw new Error(profile.error || 'Razorpay disconnect failed.');
      }

      setPaymentConfigured(Boolean(profile.paymentConfigured));
      setPaymentConnectionMode(profile.paymentConnectionMode || 'manual');
      setRazorpayAccountId(profile.razorpayAccountId || '');
      setPaymentAccess((current) => ({ ...current, razorpayConnected: false, checkoutMode: current.pinSession.active ? current.pinSession.mode || 'setup_required' : manualPaymentEnabled ? 'manual' : 'setup_required' }));
      setPaymentMessage(manualPaymentEnabled ? 'Razorpay disconnected. Customers can still pay at the counter.' : 'Razorpay disconnected. Enable pay at counter or start a PIN session before accepting new orders.');
    } catch (error) {
      setPaymentMessage(cloudErrorMessage(error, 'Razorpay disconnect'));
    } finally {
      setPaymentBusy(false);
    }
  }

  async function updatePinSession(action: 'start' | 'end') {
    if (!user) {
      setPaymentMessage('Sign in before managing a PIN session.');
      return;
    }
    try {
      setPaymentBusy(true);
      setPaymentMessage(action === 'start' ? 'Starting secure PIN session…' : 'Ending PIN session…');
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/shops/me/payment-access`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({
          action,
          mode: pinMode === 'demo_pin' ? 'DEMO_PIN' : 'COUNTER_PIN',
          durationMinutes: pinDurationMinutes,
        }),
      });
      const profile = await response.json() as ShopProfile & { error?: string };
      if (!response.ok || !profile.paymentAccess) throw new Error(profile.error || 'PIN session could not be updated.');
      setPaymentAccess(profile.paymentAccess);
      setPaymentMessage(action === 'start'
        ? `${pinMode === 'demo_pin' ? 'Demo' : 'Counter payment'} PIN is active until ${formatDateTime(profile.paymentAccess.pinSession.expiresAt)}.`
        : profile.paymentAccess.razorpayConnected
          ? 'PIN session ended. New orders will use Razorpay.'
          : 'PIN session ended. Start another session or connect Razorpay before accepting new orders.');
    } catch (error) {
      setPaymentMessage(cloudErrorMessage(error, 'PIN session'));
    } finally {
      setPaymentBusy(false);
    }
  }

  async function copyPaymentPin() {
    if (!paymentAccess.pinSession.pin) return;
    try {
      await navigator.clipboard.writeText(paymentAccess.pinSession.pin);
      setPaymentMessage('PIN copied. Share it only with customers currently at your counter.');
    } catch {
      setPaymentMessage('Could not copy the PIN. You can read it from the screen.');
    }
  }

  async function copyShopId() {
    if (!cloudShopId) return;
    try {
      await navigator.clipboard.writeText(cloudShopId);
      setShopIdCopyStatus('Copied');
    } catch {
      setShopIdCopyStatus('Copy failed');
    }
  }

  async function saveShopProfile() {
    if (!shopName.trim()) {
      setProfileMessage('Shop name is required.');
      return;
    }

    if (!city.trim()) {
      setProfileMessage('City is required.');
      return;
    }

    if (!/^\d{10}$/.test(phoneNumber.trim())) {
      setProfileMessage('Contact number must be 10 digits.');
      return;
    }

    if (!address.trim()) {
      setProfileMessage('Address is required.');
      return;
    }

    if (!pinCode.trim()) {
      setProfileMessage('PIN code is required.');
      return;
    }

    if (!/^\d{6}$/.test(pinCode.trim())) {
      setProfileMessage('PIN code must be 6 digits.');
      return;
    }

    if (!openingTime || !closingTime) {
      setProfileMessage('Opening and closing time are required.');
      return;
    }

    if (openingTime >= closingTime) {
      setProfileMessage('Closing time must be after opening time.');
      return;
    }

    if (!Number.isInteger(blackWhiteRate) || blackWhiteRate < 1 || blackWhiteRate > 100 ||
        (supportsColorPrinting && (!Number.isInteger(colorRate) || colorRate < 1 || colorRate > 500)) ||
        (supportsLegalPaper && (!Number.isInteger(legalBlackWhiteRate) || legalBlackWhiteRate < 1 || legalBlackWhiteRate > 200)) ||
        (supportsLegalPaper && supportsColorPrinting && (!Number.isInteger(legalColorRate) || legalColorRate < 1 || legalColorRate > 1000))) {
      setProfileMessage('Enter whole-rupee rates for every enabled paper and print option.');
      return;
    }

    if (onboardingRequired && !termsAccepted) {
      setProfileMessage('You must accept the shopkeeper agreements before activating the shop.');
      return;
    }

    if (!user) {
      setProfileMessage('Sign in before saving the shop profile.');
      return;
    }

    try {
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/shops/me`, {
        method: 'POST',
        headers: {
          authorization: `Bearer ${idToken}`,
          'content-type': 'application/json',
        },
        body: JSON.stringify({
          shopName,
          city,
          phoneNumber,
          address,
          pinCode,
          openingTime,
          closingTime,
          blackWhiteRate,
          colorRate,
          legalBlackWhiteRate,
          legalColorRate,
          supportsColorPrinting,
          supportsLegalPaper,
          referralCode,
          acceptTerms: termsAccepted,
        }),
      });
      if (!response.ok) {
        throw new Error(await readCloudError(response, 'Cloud profile save failed.'));
      }

      const profile = await response.json() as ShopProfile;
      if (!profile.shopId) {
        throw new Error('Cloud saved profile but did not return a shop id. Check Azure Function logs.');
      }
      setCloudShopId(profile.shopId);
      setReferralCode(profile.referralAgent?.code || referralCode);
      setReferralAgentName(profile.referralAgent?.name || referralAgentName);
      setReferralLocked(Boolean(profile.referralAgent?.code));
      setSupportContact(profile.supportContact);
      setConnectedAgentPlatform(profile.agentPlatform || '');
      setSupportPhoneNumber(profile.phoneNumber || '');
      setShopSaved(true);
      setTermsAccepted(true);
      setProfileMessage('Shop profile saved to cloud. Local agent will pick up this shop id after Google sign-in.');
    } catch (error) {
      setProfileMessage(cloudErrorMessage(error, 'Cloud profile save'));
    }
  }

  async function updatePrintingPause(paused: boolean) {
    if (!user) {
      setPrintingPauseMessage('Sign in before changing printing availability.');
      return;
    }
    if (paused && !window.confirm('Pause printing now? Customers will not be able to place new orders and the local agent will not start another print job.')) {
      return;
    }

    try {
      setPrintingPauseBusy(true);
      setPrintingPauseMessage(paused ? 'Pausing printing…' : 'Resuming printing…');
      const idToken = await user.getIdToken();
      const response = await fetch(`${cloudApiUrl}/shops/me/printing`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ paused }),
      });
      const profile = await response.json() as ShopProfile & { error?: string };
      if (!response.ok) throw new Error(profile.error || 'Printing availability could not be updated.');
      setPrintingPaused(profile.printingPaused === true);
      setPrintingPauseMessage(profile.printingPaused
        ? 'Printing is paused. New customer orders and new local-agent print starts are blocked.'
        : 'Printing is active again. The local agent can resume the queue.');
    } catch (error) {
      setPrintingPauseMessage(cloudErrorMessage(error, 'Printing availability update'));
    } finally {
      setPrintingPauseBusy(false);
    }
  }

  async function trackAgentDownload(kind: AgentDownloadKind) {
    if (!user || !cloudShopId) return;
    try {
      const idToken = await user.getIdToken();
      await fetch(`${cloudApiUrl}/shops/me/agent-download`, {
        method: 'POST',
        headers: { authorization: `Bearer ${idToken}`, 'content-type': 'application/json' },
        body: JSON.stringify({ kind }),
        keepalive: true,
      });
    } catch {
      // Download analytics must never prevent access to the agent package.
    }
  }

  async function beginAgentDownload(event: React.MouseEvent<HTMLAnchorElement>, kind: AgentDownloadKind, url: string) {
    if (!canDownloadAgent) {
      event.preventDefault();
      setShopView('profile');
      return;
    }
    event.preventDefault();
    await trackAgentDownload(kind);
    window.location.assign(url);
  }

  const profileCompletion = [shopName, city, phoneNumber, address, pinCode].filter((value) => value.trim()).length;
  const profilePercent = Math.round((profileCompletion / 5) * 100);
  const resolvedOutcomes = (adminOverview?.completedLast7Days || 0) + (adminOverview?.failedLast7Days || 0);
  const completionRate = resolvedOutcomes
    ? Math.round(((adminOverview?.completedLast7Days || 0) / resolvedOutcomes) * 100)
    : 0;
  const accessAttention = adminOverview?.shopCounts.Suspended || 0;
  const pinSessionRemainingMs = Math.max(0, new Date(paymentAccess.pinSession.expiresAt || 0).getTime() - now);
  const pinSessionActive = paymentAccess.pinSession.active && pinSessionRemainingMs > 0;
  const pinSessionRemaining = formatRemaining(pinSessionRemainingMs);
  const profileReady = profilePercent === 100 && shopSaved && (!onboardingRequired || termsAccepted);
  const paymentReady = paymentConfigured || pinSessionActive || manualPaymentEnabled;
  const subscriptionReady = subscription === 'Active';
  const agentReady = canDownloadAgent;
  const setupProgress = Math.round(([profileReady, paymentReady, subscriptionReady, agentReady].filter(Boolean).length / 4) * 100);
  const shopReady = profileReady && paymentReady && subscriptionReady && agentReady;
  const nextShopStep = !profileReady
    ? { view: 'profile' as ShopView, label: 'Complete shop details', detail: 'Add the required shop information, rates and agreements.' }
    : !subscriptionReady
      ? { view: 'support' as ShopView, label: 'Contact support', detail: 'Your free shop access needs attention.' }
      : !paymentReady
        ? { view: 'payments' as ShopView, label: 'Set up payments', detail: 'Connect Razorpay, enable pay at counter, or start a temporary PIN session.' }
        : { view: 'agent' as ShopView, label: 'Install print agent', detail: 'Choose the Android or Windows agent for the device connected to your printer.' };
  const pinSessionSetup = <>
    <label>Session type<select value={pinMode} disabled={pinSessionActive || paymentBusy} onChange={(event) => setPinMode(event.target.value as PinPaymentMode)}><option value="demo_pin">Demo · no payment</option><option value="counter_pin">Counter payment · cash/direct UPI</option></select></label>
    <label>Duration<select value={pinDurationMinutes} disabled={pinSessionActive || paymentBusy} onChange={(event) => setPinDurationMinutes(Number(event.target.value))}><option value={30}>30 minutes</option><option value={60}>1 hour</option><option value={120}>2 hours</option><option value={240}>4 hours</option><option value={480}>8 hours</option><option value={1440}>24 hours</option><option value={4320}>3 days</option><option value={10080}>7 days</option><option value={20160}>14 days</option><option value={43200}>30 days</option></select></label>
    {!pinSessionActive && <button className="secondary full" type="button" disabled={paymentBusy || !shopSaved} onClick={() => void updatePinSession('start')}>{paymentBusy ? 'Please wait…' : 'Start PIN session'}</button>}
    <small>Only orders created during this session can use its PIN. It stops working when the session ends.</small>
  </>;

  return (
    <main className={`portal ${signedIn ? 'app-portal' : `marketing-portal${adminPortal ? '' : ' compact-marketing'}`}`}>
      <header className={`site-topbar ${signedIn ? 'app-topbar' : ''}`}>
        <a className="brand" href="#top" aria-label="Saral Print home">
          <img src="/saral-print-icon.png?v=icon-1" alt="" />
          <span className="brand-wordmark" aria-hidden="true">
            <span className="brand-wordmark-saral">SARAL</span>
            <span className="brand-wordmark-print">PRINT</span>
          </span>
        </a>
        {!adminPortal && <span className="free-access-badge">FREE<span className="free-access-detail"> for shopkeepers</span></span>}
        {!signedIn && !adminPortal && <nav className="landing-nav" aria-label="Explore SaralPrint"><a href="#automation">How it works</a><a href="#printer-connections">Connect your printer</a><a href="#marketing-details">FAQs & setup</a></nav>}
        {!signedIn && !adminPortal && <button className="primary topbar-login" type="button" onClick={loginWithGoogle} disabled={authBusy}>{authBusy ? 'Opening...' : 'Login'}</button>}
        {signedIn && (
          <div className="account-menu">
            <span>{displayName}</span>
            {isAdmin && !adminPortal && <button className="admin-nav-button" type="button" onClick={() => setShowAdmin((current) => !current)}>{showAdmin ? 'My shop' : 'Platform admin'}</button>}
            <button className="icon-button" onClick={logout} aria-label="Logout" title="Logout"><Icon name="logout" /></button>
          </div>
        )}
      </header>

      {!signedIn && (
          <section className={`login-hero${adminPortal ? '' : ' compact-landing-hero'}`} id="top">
            {adminPortal && <img src={shopkeeperHero} alt="Indian print shop owner beside a multifunction printer" />}
            {!adminPortal && (
              <div className="hero-showcase">
                <section className="hero-workflow" id="automation" aria-labelledby="print-shop-automation-title">
                  <div className="search-overview-head">
                    <span>PRINT SHOP AUTOMATION</span>
                    <h2 id="print-shop-automation-title">A simpler workflow for every print counter</h2>
                  </div>
                  <div className="search-feature-grid illustrated-features">
                    <article><WorkflowCartoon scene="scan" /><strong>QR self-service ordering</strong><p>Customers scan your QR, upload a document and choose their print options.</p></article>
                    <article><WorkflowCartoon scene="pricing" /><strong>Automatic print pricing</strong><p>Your rates calculate the amount before the job reaches your counter.</p></article>
                    <article><WorkflowCartoon scene="privacy" /><strong>Private document delivery</strong><p>Paid jobs reach your Android or Windows agent. Documents are deleted automatically.</p></article>
                    <article><WorkflowCartoon scene="queue" /><strong>Print queue visibility</strong><p>Follow payment and printing status in one organized shop queue.</p></article>
                  </div>
                  <div className="search-workflow" aria-label="How SaralPrint automated printing works">
                    <div><b><Icon name="phone" /></b><span><strong>01 · Scan</strong></span></div>
                    <div><b><Icon name="route" /></b><span><strong>02 · Prepare</strong></span></div>
                    <div><b><Icon name="printer" /></b><span><strong>03 · Print</strong></span></div>
                  </div>
                </section>
                <PrinterConnectionOptions onDeviceEnquiry={enquireAboutDevice} />
              </div>
            )}
            <div className="login-card">
              <span className={adminPortal ? undefined : 'login-free-label'}>{adminPortal ? 'SaralPrint administration' : '100% free for shopkeepers'}</span>
              <h1>{adminPortal ? <>Platform monitoring.<br /><mark>One compact view.</mark></> : <>Automate your print shop.<br /><mark>Orders run on their own.</mark></>}</h1>
              <p>{adminPortal ? 'Sign in with an authorized Google account to monitor users, shops, orders, subscriptions and agent health.' : 'Turn your counter into a QR-enabled, self-service printing kiosk. Handle uploads, pricing, payment confirmation and secure print delivery.'}</p>
              {!adminPortal && <div className="mobile-print-highlight">
                <div className="mobile-print-heading">
                  <span className="mobile-android-mark" aria-hidden="true"><Icon name="android" /></span>
                  <div><span className="mobile-print-eyebrow">ANDROID PRINTING</span><h2>Use your mobile.<mark>No computer needed.</mark></h2></div>
                </div>
                <strong>Why tie up a computer when your mobile can do the job?</strong>
                <div className="mobile-print-connection" aria-label="Connect your Android phone and compatible printer to the same Wi-Fi network">
                  <div className="mobile-print-endpoint"><span><Icon name="phone" /></span><b>Android phone</b></div>
                  <div className="mobile-print-wifi"><Icon name="wifi" /><span>Same Wi-Fi</span></div>
                  <div className="mobile-print-endpoint"><span><Icon name="printer" /></span><b>Compatible printer</b></div>
                </div>
                <a className="device-hero-link" href="#print-device">Optional device? Explore Wi-Fi + SIM</a>
              </div>}
              <div className="login-benefits" aria-label="SaralPrint benefits">
                <span>{adminPortal ? 'Live platform counts' : 'Automatic pricing'}</span>
                <span>{adminPortal ? 'Operational alerts' : 'Payment confirmation'}</span>
                <span>{adminPortal ? 'Admin group control' : 'Secure print queue'}</span>
              </div>
              <button className="primary" onClick={loginWithGoogle} disabled={authBusy}>{authBusy ? 'Opening Google...' : adminPortal ? 'Continue with Google' : 'Start free with Google'}</button>
              <small>{adminPortal ? 'Only approved administrators can access this dashboard.' : 'No subscription, setup or SaralPrint per-order charges.'}</small>
              {authError && <p className="auth-error">{authError}</p>}
              {!hasFirebaseConfig && !authMessage && <p className="auth-error">Firebase config is not set for this portal build.</p>}
            </div>
          </section>
      )}

      {!signedIn && !adminPortal && <MarketingCounters apiUrl={cloudApiUrl} />}

      {!signedIn && !adminPortal && <MarketingExtras onSignup={loginWithGoogle} busy={authBusy} />}

      {signedIn && (
        <section className="dashboard-shell">
          <section className="dashboard-main" id="dashboard">
            <div className="ops-header">
              <div className="ops-identity">
                {!showAdmin && <span>{`Welcome, ${firstName}`}</span>}
                <h1>{showAdmin ? 'SaralPrint platform' : shopName || 'Your print shop'}</h1>
                {!showAdmin && user?.email && <p className="shopkeeper-email">{user.email}</p>}
                {!showAdmin && cloudShopId && (
                  <button
                    aria-label={`Copy shop ID ${cloudShopId}`}
                    className="shop-id-copy"
                    onClick={() => void copyShopId()}
                    type="button"
                  >
                    <span>Shop ID</span>
                    <strong>{cloudShopId}</strong>
                    <b aria-live="polite">{shopIdCopyStatus}</b>
                  </button>
                )}
                <p>{showAdmin ? 'Monitor growth, operations, subscriptions and access.' : 'Manage your shop setup and printing access.'}</p>
              </div>
              {!showAdmin && <div className="ops-actions">
                <span className={`shop-live-status${shopReady && !printingPaused ? ' ready' : ''}`}><i />{printingPaused ? 'Printing paused' : shopReady ? 'Ready for customers' : 'Setup in progress'}</span>
              </div>}
            </div>

            {!showAdmin && <>
            <section className={`shop-status-board${shopReady ? ' ready' : ''}${printingPaused ? ' paused' : ''}`} aria-label="Shop readiness and printing availability">
              <div className="shop-status-copy">
                <span className="shop-status-indicator" aria-hidden="true"><i /></span>
                <div>
                  <span>{printingPaused ? 'PRINTING PAUSED' : shopReady ? 'SHOP READY' : 'YOUR NEXT STEP'}</span>
                  <h2>{shopReady ? 'Everything is set up' : nextShopStep.label}</h2>
                  <p>{shopReady
                    ? printingPaused ? 'Your shop is temporarily paused' : 'Your shop is accepting print jobs'
                    : nextShopStep.detail}</p>
                </div>
              </div>
              {!shopReady && <div className="shop-status-progress" aria-label={`${setupProgress}% of shop setup complete`}>
                <strong>{setupProgress}%</strong>
                <span>Setup complete</span>
                <div><i style={{ width: `${setupProgress}%` }} /></div>
              </div>}
              <div className="shop-status-actions">
                {!shopReady && <button className="shop-status-action" type="button" onClick={() => setShopView(nextShopStep.view)}>{nextShopStep.label}</button>}
                <button className={`shop-status-toggle${printingPaused ? ' resume' : ' pause'}`} type="button" disabled={printingPauseBusy || !shopSaved} onClick={() => void updatePrintingPause(!printingPaused)}>
                  {printingPauseBusy ? 'Saving…' : printingPaused ? 'Resume printing' : 'Pause printing'}
                </button>
              </div>
              {printingPauseMessage && <small className="shop-status-message" role="status">{printingPauseMessage}</small>}
            </section>

            <nav className="portal-section-nav" aria-label="Shopkeeper portal sections">
              <button type="button" className={shopView === 'overview' ? 'active' : ''} onClick={() => setShopView('overview')}><Icon name="shield" />Overview</button>
              <button type="button" className={shopView === 'payments' ? 'active' : ''} onClick={() => setShopView('payments')}><Icon name="badge" />Payments</button>
              <button type="button" className={shopView === 'profile' ? 'active' : ''} onClick={() => setShopView('profile')}><Icon name="shop" />Shop details</button>
              <button type="button" className={shopView === 'agent' ? 'active' : ''} onClick={() => setShopView('agent')}><Icon name="download" />Print agents</button>
              <button type="button" className={shopView === 'support' ? 'active' : ''} onClick={() => setShopView('support')}><Icon name="support" />Help</button>
              {isTester && <button type="button" className={shopView === 'orders' ? 'active' : ''} onClick={() => setShopView('orders')}><Icon name="printer" />Test orders</button>}
            </nav>
            <PrinterConnectionOptions compact onDeviceEnquiry={enquireAboutDevice} onAgentSetup={openPrinterSetup} />
            </>}

            {showAdmin && adminAccess === 'checking' && <section className="panel admin-access-card"><h2>Checking administrator access…</h2><p>Loading your platform permissions and live status.</p></section>}
            {showAdmin && adminAccess === 'denied' && <section className="panel admin-access-card"><h2>Administrator access required</h2><p>{adminMessage || 'This Google account is not in the SaralPrint admin group.'}</p><button className="secondary" type="button" onClick={logout}>Use another account</button></section>}

            {adminAccess === 'granted' && showAdmin && (
              <section className="panel admin-dashboard" id="platform-admin">
                <section className="kpi-snapshot" aria-label="SaralPrint KPI snapshot">
                  <header className="kpi-snapshot-head">
                    <div>
                      <span className="kpi-eyebrow"><i /> LIVE PLATFORM KPI</span>
                      <h2>Business at a glance</h2>
                      <p>{adminOverview ? `Snapshot generated ${formatDateTime(adminOverview.generatedAt)}` : 'Loading the latest operational snapshot…'}</p>
                    </div>
                    <div className="kpi-actions">
                      <button className="kpi-share-button" type="button" disabled={!adminOverview} onClick={shareAdminSnapshot}>Share summary</button>
                      <button className="kpi-refresh-button" type="button" onClick={() => user && loadAdminOverview(user)}>Refresh KPI</button>
                    </div>
                  </header>

                  <div className="admin-status-strip" aria-label="Today's read-only platform status">
                    <article><span>Printed pages today</span><strong>{adminOverview?.printedPagesToday ?? '—'}</strong></article>
                    <article><span>Agents online</span><strong>{adminOverview?.activeAgents ?? '—'}</strong></article>
                    <article className={(adminOverview?.failedToday || 0) > 0 ? 'status-alert' : ''}><span>Failed today</span><strong>{adminOverview?.failedToday ?? '—'}</strong></article>
                  </div>

                  <div className="kpi-primary-grid">
                    <button type="button" className={`kpi-card kpi-card-orders kpi-card-action${activeAdminSection === 'orders' ? ' active' : ''}`} onClick={() => toggleAdminSection('orders')}><div className="kpi-card-icon"><Icon name="printer" /></div><div><span>Orders today</span><strong>{adminOverview?.ordersLast24Hours ?? '—'}</strong><small>{adminOverview?.ordersLast7Days ?? 0} this week · View records</small></div></button>
                    <button type="button" className={`kpi-card kpi-card-success kpi-card-action${activeAdminSection === 'audit' ? ' active' : ''}`} onClick={() => toggleAdminSection('audit')}><div className="kpi-card-icon"><Icon name="shield" /></div><div><span>Completed · 7 days</span><strong>{adminOverview?.completedLast7Days ?? '—'}</strong><small>{completionRate}% successful · View audit</small></div></button>
                    <button type="button" className={`kpi-card kpi-card-shops kpi-card-action${activeAdminSection === 'shops' ? ' active' : ''}`} onClick={() => toggleAdminSection('shops')}><div className="kpi-card-icon"><Icon name="shop" /></div><div><span>Shopkeepers</span><strong>{adminOverview?.shops ?? '—'}</strong><small>{adminOverview?.shopCounts.Active ?? 0} active · {accessAttention} suspended</small></div></button>
                    <button type="button" className={`kpi-card kpi-card-action${activeAdminSection === 'customers' ? ' active' : ''}`} onClick={() => toggleAdminSection('customers')}><div className="kpi-card-icon"><Icon name="users" /></div><div><span>Customers</span><strong>{adminOverview?.customers ?? '—'}</strong><small>{adminOverview?.testCustomers ?? 0} tester accounts excluded</small></div></button>
                    <button type="button" className={`kpi-card kpi-card-users kpi-card-action${activeAdminSection === 'salesAgents' ? ' active' : ''}`} onClick={() => toggleAdminSection('salesAgents')}><div className="kpi-card-icon"><Icon name="users" /></div><div><span>Sales agents</span><strong>{adminOverview?.activeAgents ?? '—'}</strong><small>Online now · Manage agents</small></div></button>
                    <button type="button" className={`kpi-card kpi-card-action${activeAdminSection === 'supportRequests' ? ' active' : ''}`} onClick={() => toggleAdminSection('supportRequests')}><div className="kpi-card-icon"><Icon name="support" /></div><div><span>Support inbox</span><strong>{adminSections.supportRequests === 'loaded' ? adminSupportRequests.length : 'View'}</strong><small>Queries and suggestions · 30-day retention</small></div></button>
                    <AdminChatButton key={user?.uid} apiUrl={cloudApiUrl} user={user} active={activeAdminSection === 'chats'} onClick={() => toggleAdminSection('chats')} />
                    <button type="button" className={`kpi-card kpi-card-action${activeAdminSection === 'users' ? ' active' : ''}`} onClick={() => toggleAdminSection('users')}><div className="kpi-card-icon"><Icon name="users" /></div><div><span>Admin access</span><strong>Manage</strong><small>Authorized platform accounts</small></div></button>
                  </div>

                  <footer className="kpi-snapshot-footer"><span>Operational data · No additional analytics storage</span><strong>{formatCurrency(adminOverview?.completedValue || 0)} completed value</strong></footer>
                </section>
                <small className="form-message">{adminShareMessage || adminMessage}</small>

                {activeAdminSection && <div className="admin-modal-backdrop" role="presentation" onMouseDown={() => setActiveAdminSection(null)}>
                  <section className="admin-modal" role="dialog" aria-modal="true" aria-labelledby="admin-modal-title" onMouseDown={(event) => event.stopPropagation()}>
                    <header className="admin-modal-head">
                      <div><h2 id="admin-modal-title">{adminSectionDetails[activeAdminSection].title}</h2><p>{adminSectionDetails[activeAdminSection].description}</p></div>
                      <button type="button" className="secondary" onClick={() => setActiveAdminSection(null)}>Close</button>
                    </header>
                    <div className="admin-modal-body">
                {activeAdminSection === 'users' && <section className="admin-content-panel">
                  {adminSections.users === 'loading' && <p className="lazy-loading">Loading administrator accounts…</p>}
                  {adminSections.users === 'error' && <button className="secondary" type="button" onClick={() => user && loadAdminUsers(user, true)}>Retry · {adminSectionErrors.users}</button>}
                  {adminSections.users === 'loaded' && <section className="admin-section">
                    <div><p>Grant access using an existing SaralPrint Google account. Firebase Auth stores the role; no separate user database is created.</p></div>
                    <div className="admin-user-form">
                      <label>Google account email<input type="email" value={adminEmail} onChange={(event) => setAdminEmail(event.target.value)} placeholder="name@gmail.com" autoCapitalize="none" autoComplete="email" /></label>
                      <button className="primary" type="button" disabled={!adminManagementConfigured || adminUserBusy || !adminEmail.trim()} onClick={grantAdminAccess}>{adminUserBusy ? 'Adding…' : 'Add administrator'}</button>
                    </div>
                    {!adminManagementConfigured && <small className="form-message">Role editing becomes available after the Firebase service-account secret is configured on the Azure Function App.</small>}
                    {adminUserMessage && <small className="form-message">{adminUserMessage}</small>}
                    <div className="admin-user-list">
                      {adminUsers.map((admin) => <article key={admin.uid || admin.email}><span className="admin-avatar"><Icon name="users" /></span><div><strong>{admin.displayName || admin.email}</strong><small>{admin.email}</small><small>{admin.lastSignInTime ? `Last sign-in ${formatDateTime(admin.lastSignInTime)}` : 'Not signed in yet'}</small></div><b>{admin.source === 'configuration' ? 'Configured' : 'Admin'}</b></article>)}
                    </div>
                  </section>}
                </section>}

                {activeAdminSection === 'shops' && <section className="admin-content-panel">
                  {adminMessage && <small className="form-message" role="status">{adminMessage}</small>}
                  {adminSections.shops === 'loading' && <p className="lazy-loading">Loading shop details…</p>}
                  {adminSections.shops === 'error' && <button className="secondary" type="button" onClick={() => user && loadAdminShops(user, true)}>Retry · {adminSectionErrors.shops}</button>}
                  {adminSections.shops === 'loaded' && <AdminShopsTable
                    shops={adminShops}
                    overallTotal={adminOverview?.shops}
                    filter={adminShopFilter}
                    identityDeletionConfigured={adminIdentityDeletionConfigured}
                    onFilterChange={(filter) => {
                      setAdminShopFilter(filter);
                      if (user) void loadAdminShops(user, true, filter);
                    }}
                    onToggleTest={updateTestAccount}
                    onSubscription={updateAdminSubscription}
                    onPrintingPause={updateAdminPrintingPause}
                    onDelete={deleteAdminShop}
                  />}
                </section>}

                {activeAdminSection === 'customers' && <section className="admin-content-panel">
                  {adminSections.customers === 'loading' && <p className="lazy-loading">Loading customer accounts…</p>}
                  {adminSections.customers === 'error' && <button className="secondary" type="button" onClick={() => user && loadAdminCustomers(user, true)}>Retry · {adminSectionErrors.customers}</button>}
                  {adminSections.customers === 'loaded' && <div className="admin-card-list">{adminCustomers.map((customer) => <article key={customer.uid}><div><strong>{customer.displayName || customer.email || customer.uid}</strong><small>{customer.email || 'Email not available'}</small><small>{customer.isShopkeeper ? 'Shopkeeper account' : 'Customer account'}</small></div><div className="admin-card-facts"><b>{customer.isTestAccount ? 'TESTER' : 'CUSTOMER'}</b><small>{customer.lastLoginAt ? `Last sign-in ${formatDateTime(customer.lastLoginAt)}` : 'Not signed in yet'}</small></div></article>)}{!adminCustomers.length && <div className="empty">No customer accounts found.</div>}</div>}
                </section>}

                {activeAdminSection === 'salesAgents' && <section className="admin-content-panel">
                  <div className="form-grid compact-form">
                    {editingSalesAgentCode && <label>Agent code<input value={editingSalesAgentCode} readOnly /></label>}
                    <label>Name<input value={salesAgentName} onChange={(event) => setSalesAgentName(event.target.value)} placeholder="Full name" /></label>
                    <label>Mobile<input value={salesAgentPhone} onChange={(event) => setSalesAgentPhone(event.target.value.replace(/\D/g, '').slice(0, 10))} placeholder="9876543210" /></label>
                    <label>City<input value={salesAgentCity} onChange={(event) => setSalesAgentCity(event.target.value)} placeholder="Pune" /></label>
                    <label>Email (optional)<input type="email" value={salesAgentEmail} onChange={(event) => setSalesAgentEmail(event.target.value)} placeholder="agent@example.com" /></label>
                  </div>
                  <div className="admin-card-actions"><button type="button" className="secondary" disabled={!salesAgentName.trim() || salesAgentPhone.length !== 10 || !salesAgentCity.trim()} onClick={() => void (editingSalesAgentCode ? saveSalesAgent() : createSalesAgent())}>{editingSalesAgentCode ? 'Save agent' : 'Create agent and code'}</button>{editingSalesAgentCode && <button type="button" onClick={cancelSalesAgentEdit}>Cancel edit</button>}</div>
                  {salesAgentMessage && <small className="form-message">{salesAgentMessage}</small>}
                  {adminSections.salesAgents === 'loading' && <p className="lazy-loading">Loading agents…</p>}
                  {adminSections.salesAgents === 'loaded' && <div className="admin-card-list">{salesAgents.map((agent) => { const shareText = salesAgentShareText(agent); return <article key={agent.code}><div><strong>{agent.name}</strong><small>{agent.city} · {agent.phoneNumber}</small>{agent.email && <small>{agent.email}</small>}</div><div className="admin-card-facts"><b>{agent.active ? 'Active' : 'Inactive'}</b><strong>{agent.code}</strong></div><div className="admin-card-actions"><button type="button" onClick={() => startSalesAgentEdit(agent)}>Edit</button><button type="button" onClick={() => void copySalesAgentCard(agent)}>Copy card</button><a className="secondary link" href={`https://wa.me/?text=${encodeURIComponent(shareText)}`} target="_blank" rel="noreferrer">WhatsApp</a><a className="secondary link" href={`mailto:?subject=${encodeURIComponent(`SaralPrint agent ${agent.code}`)}&body=${encodeURIComponent(shareText)}`}>Email</a></div></article>; })}{!salesAgents.length && <div className="empty">No sales agents yet.</div>}</div>}
                </section>}

                {activeAdminSection === 'orders' && <section className="admin-content-panel">
                  {adminSections.orders === 'loading' && <p className="lazy-loading">Loading recent orders…</p>}
                  {adminSections.orders === 'error' && <button className="secondary" type="button" onClick={() => user && loadAdminOrders(user, true)}>Retry · {adminSectionErrors.orders}</button>}
                  {adminSections.orders === 'loaded' && <AdminOrdersTable
                    orders={adminOrders}
                    totalCount={adminOrderTotalCount}
                    retention={adminOrderRetention}
                    testControlsAvailable={testControlsAvailable}
                    onMockPaid={markTestOrderPaid}
                  />}
                </section>}

                {activeAdminSection === 'audit' && <section className="admin-content-panel">
                  {adminSections.audit === 'loading' && <p className="lazy-loading">Loading audit history…</p>}
                  {adminSections.audit === 'error' && <button className="secondary" type="button" onClick={() => user && loadAdminAudit(user, true)}>Retry · {adminSectionErrors.audit}</button>}
                  {adminSections.audit === 'loaded' && <div className="audit-list">
                    {adminAudit.slice(0, 30).map((event) => <div key={event.id}><strong>{event.action}</strong><span>{event.targetType} {event.targetId}</span><small>{event.actorEmail} · {formatDateTime(event.createdAt)}</small></div>)}
                    {!adminAudit.length && <div className="empty">No administrator actions recorded yet.</div>}
                  </div>}
                </section>}

                {activeAdminSection === 'chats' && <AdminChatInbox key={user?.uid} apiUrl={cloudApiUrl} user={user} />}

                {activeAdminSection === 'supportRequests' && <section className="admin-content-panel">
                  {adminSections.supportRequests === 'loading' && <p className="lazy-loading">Loading support requests…</p>}
                  {adminSections.supportRequests === 'error' && <button className="secondary" type="button" onClick={() => user && loadAdminSupportRequests(user, true)}>Retry · {adminSectionErrors.supportRequests}</button>}
                  {adminSections.supportRequests === 'loaded' && <div className="admin-card-list support-request-list">
                    {adminSupportRequests.map((request) => <article key={request.id}>
                      <div><strong>{request.type === 'suggestion' ? 'Suggestion' : 'Query'} · {request.shopName}</strong><small>{request.source === 'shopkeeper' ? `${request.shopId} · ${request.shopEmail}` : 'Public support page'}</small><p>{request.message}</p></div>
                      <div className="admin-card-facts"><small>{countryName(request.country)} · +{request.callingCode || '91'}</small><a href={`tel:${request.phoneNumber.startsWith('+') ? request.phoneNumber : `+${request.callingCode || '91'}${request.phoneNumber}`}`}>📞 {request.phoneNumber.startsWith('+') ? request.phoneNumber : `+${request.callingCode || '91'} ${request.phoneNumber}`}</a><small>Received {formatDateTime(request.createdAt)}</small><small>Deletes {formatDateTime(request.expiresAt)}</small></div>
                    </article>)}
                    {!adminSupportRequests.length && <div className="empty">No support requests in the last 30 days.</div>}
                  </div>}
                </section>}
                    </div>
                  </section>
                </div>}
              </section>
            )}

            {!showAdmin && <section className="mobile-stack portal-workspace" data-view={shopView}>
              <section className="overview-grid view-overview" aria-label="Shop setup overview">
                <article className="panel readiness-panel">
                  <div className="panel-head">
                    <div><span className="section-eyebrow">READINESS</span><h2>What is ready</h2><p>Finish only the items that need your attention.</p></div>
                    <b className={shopReady ? 'status-good' : ''}>{shopReady ? 'All ready' : `${setupProgress}% complete`}</b>
                  </div>
                  <div className="readiness-list">
                    <button type="button" onClick={() => setShopView('profile')}>
                      <span className={`readiness-icon${profileReady ? ' complete' : ''}`}><Icon name="shop" /></span>
                      <span><strong>Shop details</strong><small>{profileReady ? 'Saved and ready' : `${profilePercent}% filled · action needed`}</small></span>
                      <b>{profileReady ? 'Ready' : 'Fix'}</b>
                    </button>
                    <button type="button" onClick={() => setShopView('payments')}>
                      <span className={`readiness-icon${paymentReady ? ' complete' : ''}`}><Icon name="badge" /></span>
                      <span><strong>Customer payments</strong><small>{pinSessionActive ? `PIN active · ${pinSessionRemaining} left` : paymentConfigured ? 'Razorpay connected' : 'Choose how customers pay'}</small></span>
                      <b>{paymentReady ? 'Ready' : 'Set up'}</b>
                    </button>
                    <button type="button" onClick={() => setShopView('agent')}>
                      <span className={`readiness-icon${agentReady ? ' complete' : ''}`}><Icon name="download" /></span>
                      <span><strong>Print agent access</strong><small>{agentReady ? 'Android and Windows downloads' : 'Complete shop details first'}</small></span>
                      <b>{agentReady ? 'Available' : 'Locked'}</b>
                    </button>
                    <div className="readiness-static">
                      <span className={`readiness-icon${subscriptionReady ? ' complete' : ''}`}><Icon name="shield" /></span>
                      <span><strong>Free shop access</strong><small>{subscriptionPlan} · no subscription charge</small></span>
                      <b>{subscriptionReady ? 'Active' : subscription}</b>
                    </div>
                  </div>
                </article>

                <aside className="panel quick-actions-panel">
                  <span className="section-eyebrow">QUICK ACTIONS</span>
                  <h2>Common tasks</h2>
                  <button type="button" onClick={() => setShopView('payments')}><Icon name="badge" /><span><strong>Manage payments</strong><small>Razorpay and temporary PIN</small></span></button>
                  <button type="button" onClick={() => setShopView('profile')}><Icon name="shop" /><span><strong>Update rates</strong><small>Paper, colour and page prices</small></span></button>
                  <button type="button" onClick={() => setShopView('agent')}><Icon name="download" /><span><strong>Get print agent</strong><small>Android APK and Windows installer</small></span></button>
                  <button type="button" onClick={() => setShopView('support')}><Icon name="support" /><span><strong>Get help</strong><small>Contact your support person</small></span></button>
                </aside>
              </section>

              {isTester && <details className="panel test-tools view-orders" id="orders">
                <summary>Tester tools and mock payments</summary>
                <div className="panel-head">
                  <div><h2>Test orders</h2><p>Approve mock payments only while validating the local-agent flow.</p></div>
                  <button className="secondary" type="button" onClick={() => user && loadMyOrders(user)}>Refresh</button>
                </div>
                <OrderTable orders={shopOrders} canMockPay={isTester && testControlsAvailable} onMockPay={markTestOrderPaid} now={now} />
                <small className="form-message">{orderMessage}</small>
              </details>}

              <article className="panel payments-panel view-payments" id="payments">
                <div className="panel-head">
                  <div>
                    <h2>Customer payments</h2>
                    <p>Razorpay payments run online. Counter orders wait for your approval in the Android or Windows agent before printing.</p>
                  </div>
                  <b>{pinSessionActive ? 'PIN active' : paymentConfigured ? 'Razorpay connected' : manualPaymentEnabled ? 'Counter payment active' : 'Setup needed'}</b>
                </div>

                <section className="payment-online-card">
                  <div><span>OPTIONAL FOR CUSTOMERS</span><h3>Pay at counter</h3></div>
                  <p>Allow customers without UPI to place an order and pay you directly. The order prints only after you confirm payment in the Android or Windows agent. This can stay on alongside Razorpay.</p>
                  <button className="secondary" type="button" disabled={paymentBusy || !shopSaved} onClick={() => void updateManualPayment(!manualPaymentEnabled)}>{paymentBusy ? 'Saving…' : manualPaymentEnabled ? 'Disable pay at counter' : 'Enable pay at counter'}</button>
                </section>

                {pinSessionActive && <div className={`payment-pin-card ${paymentAccess.pinSession.mode}`}>
                  <span>{paymentAccess.pinSession.mode === 'demo_pin' ? 'DEMO SESSION · NO MONEY' : 'COUNTER PAYMENT SESSION'}</span>
                  <strong>{paymentAccess.pinSession.pin || '••••••'}</strong>
                  <p>{paymentAccess.pinSession.mode === 'demo_pin'
                    ? 'Customers enter this PIN to test the full print flow. These orders collect ₹0 and are marked as demo.'
                    : 'Collect cash or direct UPI first. The customer then enters this PIN to release the job.'}</p>
                  <small>Ends in {pinSessionRemaining} · {formatDateTime(paymentAccess.pinSession.expiresAt)}</small>
                  <div className="payment-actions"><button className="secondary" type="button" onClick={() => void copyPaymentPin()}>Copy PIN</button><button className="danger" type="button" disabled={paymentBusy} onClick={() => void updatePinSession('end')}>End session</button></div>
                </div>}

                {paymentConfigured ? <div className="payment-connected-flow">
                  <section className="payment-online-card">
                    <div><span>ONLINE PAYMENTS</span><h3>Razorpay is connected</h3></div>
                    <p>Customers pay in Razorpay checkout. Once Razorpay confirms payment, the print job is released automatically.</p>
                    <a className="secondary link" href={`${merchantPortalUrl}/?portal=saralprint#payments`}>Manage Razorpay</a>
                  </section>
                  {!pinSessionActive && <details className="payment-fallback">
                    <summary><span>Need a demo or cash payment instead?</span><small>Start a temporary PIN session</small></summary>
                    <div className="payment-fallback-content">{pinSessionSetup}</div>
                  </details>}
                </div> : <div className="payment-choice-grid">
                  <section>
                    <h3>Set up online payments</h3>
                    <p>Connect your normal Razorpay Payments merchant account to let customers pay online.</p>
                    <button className="primary full" type="button" disabled={!shopSaved} onClick={() => void connectRazorpay()}>Connect Razorpay</button>
                    {hasRazorpayMerchantSignupUrl && <a className="secondary link full" href={razorpayMerchantSignupUrl} target="_blank" rel="noreferrer">Create a Razorpay merchant account</a>}
                  </section>
                  <section>
                    <h3>Use a temporary PIN</h3>
                    <p>For a demo or a payment you collect directly from the customer.</p>
                    {pinSessionSetup}
                  </section>
                </div>}
                {paymentMessage && <small className="form-message" role="status">{paymentMessage}</small>}
                <p className="payment-safety-note">Demo PIN does not collect money. A counter PIN records payment collected outside SaralPrint. Razorpay orders are released only after Razorpay confirms payment.</p>
              </article>

              <article className="panel profile-panel view-profile" id="profile">
                <div className="panel-head">
                  <div>
                    <h2>Shop Profile</h2>
                    <p>Customers and the local agent use this profile.</p>
                  </div>
                  <b>{canActivate ? 'Ready' : 'Setup'}</b>
                </div>
                <div className="form-grid">
                  <label>Shop name *<input required value={shopName} onChange={(event) => { setShopName(event.target.value); setShopSaved(false); }} placeholder="Example: Sai Xerox & Print" /></label>
                  <label>City *<input required value={city} onChange={(event) => { setCity(event.target.value); setShopSaved(false); }} placeholder="Example: Pune" /></label>
                  <label>Contact number *<input required type="tel" value={phoneNumber} onChange={(event) => { setPhoneNumber(event.target.value); setShopSaved(false); }} placeholder="9876543210" maxLength={10} /></label>
                  <label>PIN code *<input required value={pinCode} onChange={(event) => { setPinCode(event.target.value); setShopSaved(false); }} placeholder="411001" maxLength={6} /></label>
                  <label className="wide-field">Address *<input required value={address} onChange={(event) => { setAddress(event.target.value); setShopSaved(false); }} placeholder="123 MG Road, Near Station" /></label>
                </div>
                <div className="referral-card">
                  <div><strong>Onboarding agent (optional)</strong><small>Enter the agent code shared during registration. Once saved, this assignment is locked to keep commission and support records accurate.</small></div>
                  <label>Agent code<input value={referralCode} disabled={referralLocked} onChange={(event) => { setReferralCode(event.target.value.toUpperCase()); setReferralAgentName(''); setReferralMessage(''); setShopSaved(false); }} onBlur={() => void verifyReferralCode()} placeholder="AG-ABC123" maxLength={9} /></label>
                  {!referralAgentName && referralCode.trim() && <button type="button" className="secondary" onClick={() => void verifyReferralCode()}>Verify code</button>}
                  {referralAgentName && <b>Assigned to {referralAgentName}</b>}
                  {referralMessage && <small className="form-message">{referralMessage}</small>}
                </div>
                <h3>Working Hours</h3>
                <div className="form-grid compact-form">
                  <label>Opening time<input type="time" value={openingTime} onChange={(event) => { setOpeningTime(event.target.value); setShopSaved(false); }} /></label>
                  <label>Closing time<input type="time" value={closingTime} onChange={(event) => { setClosingTime(event.target.value); setShopSaved(false); }} /></label>
                </div>
                <h3>Print Capabilities</h3>
                <div className="paper-size-settings">
                  <div><strong>A4</strong><span>Always available for standard printers</span></div>
                  <label className="paper-size-toggle"><input type="checkbox" checked={supportsColorPrinting} onChange={(event) => { setSupportsColorPrinting(event.target.checked); setShopSaved(false); }} /><span><strong>Colour printing</strong><small>Enable only when a colour printer is available</small></span></label>
                  <label className="paper-size-toggle"><input type="checkbox" checked={supportsLegalPaper} onChange={(event) => { setSupportsLegalPaper(event.target.checked); setShopSaved(false); }} /><span><strong>Legal</strong><small>Enable only when Legal paper is loaded and supported</small></span></label>
                </div>
                <h3>Customer Print Rates</h3>
                <div className="rate-matrix">
                  <div className="rate-matrix-head"><strong>Paper</strong><strong>B&amp;W / page</strong>{supportsColorPrinting && <strong>Colour / page</strong>}</div>
                  <div className="rate-matrix-row"><strong>A4</strong><label>₹<input aria-label="A4 B&W rate" type="number" min="1" max="100" step="1" value={blackWhiteRate} onChange={(event) => { setBlackWhiteRate(Number(event.target.value)); setShopSaved(false); }} /></label>{supportsColorPrinting && <label>₹<input aria-label="A4 Colour rate" type="number" min="1" max="500" step="1" value={colorRate} onChange={(event) => { setColorRate(Number(event.target.value)); setShopSaved(false); }} /></label>}</div>
                  {supportsLegalPaper && <div className="rate-matrix-row"><strong>Legal</strong><label>₹<input aria-label="Legal B&W rate" type="number" min="1" max="200" step="1" value={legalBlackWhiteRate} onChange={(event) => { setLegalBlackWhiteRate(Number(event.target.value)); setShopSaved(false); }} /></label>{supportsColorPrinting && <label>₹<input aria-label="Legal Colour rate" type="number" min="1" max="1000" step="1" value={legalColorRate} onChange={(event) => { setLegalColorRate(Number(event.target.value)); setShopSaved(false); }} /></label>}</div>}
                </div>
                <div className="agreement-box">
                  <label>
                    <input type="checkbox" checked={termsAccepted} onChange={(event) => { setTermsAccepted(event.target.checked); setShopSaved(false); }} />
                    <span><strong>I agree to the shopkeeper agreements</strong><small>I confirm that the shop details and print rates are accurate, I will operate the local agent securely, collect payment from customers as selected, and handle documents only for printing and fulfilment.</small></span>
                  </label>
                  <small>By continuing, you accept SaralPrint’s <a href="/terms" target="_blank" rel="noreferrer">User Agreement</a>, <a href="/privacy" target="_blank" rel="noreferrer">Privacy Policy</a>, acceptable-use rules and responsibility for customer documents, pricing and payments.</small>
                </div>
                <button className="primary full" type="button" onClick={saveShopProfile}>Save shop registration</button>
                {profileMessage && <small className="form-message">{profileMessage}</small>}
              </article>

              <div className="side-grid portal-side-views">
                <article className="panel view-support" id="support">
                  <h2>Need help?</h2>
                  <p>Chat with SaralPrint · Live support 10 AM–6 PM IST, every day. Leave a message anytime.</p>
                  <button className="primary" type="button" onClick={() => { setChatDraft(''); setChatOpenSignal(value => value + 1); }}>Open support chat</button>
                  {supportContact ? <><p>{supportContact.message}</p><div className="token"><span>{supportContact.type === 'sales_agent' ? 'Your onboarding agent' : 'SaralPrint support'}</span><strong>{supportContact.name}</strong>{supportContact.phoneNumber && <a href={`tel:${supportContact.phoneNumber}`}>{supportContact.phoneNumber}</a>}</div>{supportContact.availableUntil && <small>Agent support until {formatDate(supportContact.availableUntil)}. After that, SaralPrint call-centre support will take over.</small>}</> : <p>Save your registration to see the correct support contact for your shop.</p>}
                  <div className="support-request-form">
                    <h3>Send a message</h3>
                    <p>Share a query or suggestion. Our team will get back to you.</p>
                    <CountryPhoneInput country={supportCountry} phone={supportPhoneNumber} onCountry={setSupportCountry} onPhone={setSupportPhoneNumber} disabled={supportRequestBusy} />
                    <label>Message type<select value={supportRequestType} onChange={(event) => setSupportRequestType(event.target.value as 'query' | 'suggestion')}><option value="query">Query</option><option value="suggestion">Suggestion</option></select></label>
                    <label>Your message<textarea rows={4} maxLength={2000} value={supportRequestMessage} onChange={(event) => setSupportRequestMessage(event.target.value)} placeholder="How can we help?" /></label>
                    <button className="primary full" type="button" disabled={supportRequestBusy || !possibleContactPhone(supportPhoneNumber, supportCountry) || supportRequestMessage.trim().length < 5} onClick={submitShopSupportRequest}>{supportRequestBusy ? 'Sending…' : 'Send to SaralPrint'}</button>
                    {supportRequestStatus && <small className="form-message" role="status">{supportRequestStatus}</small>}
                    <small>Text only. Do not include passwords, payment keys, card details or customer documents. Requests are deleted after 30 days.</small>
                  </div>
                </article>
                <article className="panel view-agent" id="agent">
                  <h2>Print agents</h2>
                  <p>Choose the device that will stay with your printers.</p>
                  {cloudShopId ? <div className="token"><span>Cloud shop id</span><strong>{cloudShopId}</strong></div> : <div className="empty">Save profile to create a cloud shop id</div>}
                  <div className="agent-connected-type"><AgentTypeBadge platform={connectedAgentPlatform} /></div>
                  <div className="agent-platform-grid">
                    <section className="agent-platform-card android-agent-card" id="android-print-agent" tabIndex={-1}>
                      <div className="agent-platform-heading"><span className="agent-platform-icon"><Icon name="android" /></span><div><small>EARLY ACCESS</small><h3>Android agent</h3></div></div>
                      <p>Use an Android phone that stays on the same Wi-Fi network as your printer.</p>
                      <a className="primary link icon-link full" href={canDownloadAgent ? androidAgentApkUrl : '#profile'} onClick={(event) => void beginAgentDownload(event, 'android_apk', androidAgentApkUrl)} aria-label={canDownloadAgent ? 'Download Android agent APK' : 'Complete shop profile before downloading the Android agent'}><Icon name="download" /> {canDownloadAgent ? 'Download Android APK' : 'Complete setup first'}</a>
                      <div className="early-access-notice"><strong>For setup testing</strong><span>Sign-in, Wi-Fi printer discovery and test printing are ready. Automatic live-order printing is still being built.</span></div>
                      <div className="play-store-notice"><strong>Play Store coming soon</strong><span>For now, Android may ask you to allow installation from this browser. Install only the APK downloaded from this portal.</span></div>
                    </section>
                    <section className="agent-platform-card" id="windows-print-agent" tabIndex={-1}>
                      <div className="agent-platform-heading"><span className="agent-platform-icon windows"><Icon name="windows" /></span><div><small>WINDOWS 10 / 11</small><h3>Windows agent</h3></div></div>
                      <p>Use the shop computer connected to your installed Windows printers.</p>
                      <div className="download-actions">
                        <a className="secondary link icon-link" href={canDownloadAgent ? agentInstallerUrl : '#profile'} onClick={(event) => void beginAgentDownload(event, 'installer', agentInstallerUrl)} aria-label={canDownloadAgent ? 'Download Windows installer' : 'Complete shop profile before downloading the Windows installer'}><Icon name="download" /> {canDownloadAgent ? 'Installer' : 'Complete setup'}</a>
                        <a className="secondary link icon-link" href={canDownloadAgent ? agentZipUrl : '#profile'} onClick={(event) => void beginAgentDownload(event, 'portable_zip', agentZipUrl)} aria-label={canDownloadAgent ? 'Download portable ZIP' : 'Complete shop profile before downloading the portable ZIP'}><Icon name="archive" /> Portable ZIP</a>
                      </div>
                      <small className="download-help">If Windows shows a security warning, choose More info, then Run anyway.</small>
                    </section>
                  </div>
                </article>

                {showShopkeeperSubscription && <article className="panel view-agent" id="subscription">
                  <div className="panel-head">
                    <div>
                      <h2>Free shop access</h2>
                      <p>{subscriptionMessage}</p>
                    </div>
                    <b>{subscription}</b>
                  </div>
                  <button className="secondary full" onClick={checkSubscriptionStatus}>Refresh status</button>
                  <p>No SaralPrint subscription or per-order platform charge applies to your shop.</p>
                </article>}
              </div>
            </section>}
          </section>
        </section>
      )}
      {!adminPortal && !showAdmin && <SupportChatWidget key={user?.uid || 'guest'} apiUrl={cloudApiUrl} user={user} openSignal={chatOpenSignal} draftMessage={chatDraft} onDraftUsed={() => setChatDraft('')} />}
    </main>
  );
}

type AdminShopFilter = 'production' | 'incomplete' | 'test' | 'all';
type AdminShopSort = 'registered' | 'name' | 'city' | 'status' | 'prints';

function AdminOrdersTable({ orders, totalCount, retention, testControlsAvailable, onMockPaid }: {
  orders: PortalOrder[];
  totalCount: number;
  retention: OrderRetention | null;
  testControlsAvailable: boolean;
  onMockPaid: (order: PortalOrder) => Promise<void>;
}) {
  const [shopQuery, setShopQuery] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const statuses = useMemo(() => Array.from(new Set(orders.map((order) => order.status).filter(Boolean))).sort(), [orders]);
  const filteredOrders = useMemo(() => {
    const normalizedShop = shopQuery.trim().toLocaleLowerCase();
    const fromTime = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : Number.NEGATIVE_INFINITY;
    const toTime = dateTo ? new Date(`${dateTo}T23:59:59.999`).getTime() : Number.POSITIVE_INFINITY;
    return orders.filter((order) => {
      const createdAt = new Date(order.createdAt).getTime();
      const shopMatches = !normalizedShop || `${order.shopName || ''} ${order.shopId}`.toLocaleLowerCase().includes(normalizedShop);
      return shopMatches && (!status || order.status === status) && createdAt >= fromTime && createdAt <= toTime;
    }).sort((left, right) => {
      const comparison = new Date(left.createdAt).getTime() - new Date(right.createdAt).getTime();
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [orders, shopQuery, status, dateFrom, dateTo, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(filteredOrders.length / pageSize));
  const visibleOrders = filteredOrders.slice((page - 1) * pageSize, page * pageSize);
  const rangeStart = filteredOrders.length ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, filteredOrders.length);
  const filtersActive = Boolean(shopQuery || status || dateFrom || dateTo);

  useEffect(() => setPage(1), [shopQuery, status, dateFrom, dateTo, sortDirection, pageSize]);
  useEffect(() => setPage((current) => Math.min(current, totalPages)), [totalPages]);

  function clearFilters() {
    setShopQuery('');
    setStatus('');
    setDateFrom('');
    setDateTo('');
  }

  return <div className="admin-orders-view">
    <aside className="order-retention-note" aria-label="Order data retention">
      <strong>Live-list retention</strong>
      <span>Completed, collected and failed order details become eligible for archival after {retention?.terminalMetadataMinutes ?? 30} minutes. Cleanup runs every {retention?.cleanupIntervalHours ?? 6} hours, so removal is not immediate.</span>
      <span>Print documents are kept for at most {retention?.documentMaximumHours ?? 24} hours. Only aggregate daily totals remain for reporting after detailed order metadata is removed.</span>
    </aside>

    <div className="admin-order-filters" aria-label="Order filters">
      <label className="order-shop-filter">Shopkeeper name or ID
        <input value={shopQuery} onChange={(event) => setShopQuery(event.target.value)} placeholder="Search shopkeeper" />
      </label>
      <label>Status
        <select value={status} onChange={(event) => setStatus(event.target.value)}>
          <option value="">All statuses</option>
          {statuses.map((orderStatus) => <option key={orderStatus} value={orderStatus}>{orderStatus.replaceAll('_', ' ')}</option>)}
        </select>
      </label>
      <label>From date
        <input type="date" value={dateFrom} max={dateTo || undefined} onChange={(event) => setDateFrom(event.target.value)} />
      </label>
      <label>To date
        <input type="date" value={dateTo} min={dateFrom || undefined} onChange={(event) => setDateTo(event.target.value)} />
      </label>
      <button type="button" className="secondary" disabled={!filtersActive} onClick={clearFilters}>Clear filters</button>
    </div>

    <div className="admin-shop-toolbar">
      <p className="admin-shop-count"><strong>{filteredOrders.length}</strong> matching order{filteredOrders.length === 1 ? '' : 's'} in the latest {orders.length}{totalCount > orders.length ? ` of ${totalCount}` : ''} currently retained</p>
      <div className="admin-shop-sort-controls">
        <label>Order
          <select value={sortDirection} onChange={(event) => setSortDirection(event.target.value as 'asc' | 'desc')}>
            <option value="desc">Newest first</option>
            <option value="asc">Oldest first</option>
          </select>
        </label>
      </div>
    </div>

    {visibleOrders.length ? <div className="admin-table-wrap admin-order-table-wrap">
      <table className="admin-table admin-order-table">
        <thead><tr><th>Shopkeeper</th><th>Document</th><th>Print details</th><th>Status</th><th>Placed</th><th>Amount</th>{testControlsAvailable && <th>Actions</th>}</tr></thead>
        <tbody>{visibleOrders.map((order) => {
          const canMockPay = ['AWAITING_PAYMENT', 'PAYMENT_PENDING', 'PAYMENT_EXPIRED'].includes(order.status);
          return <tr key={`${order.shopId}-${order.orderId}`}>
            <td><strong>{order.shopName || order.shopId}</strong><small>{order.shopId}</small></td>
            <td><strong>{order.fileName}</strong><small>{order.orderId}</small></td>
            <td><strong>{order.pageRange ? `${order.pageRange} (${order.pages}p)` : `${order.pages}p`} × {order.copies || 1}</strong><small>{order.mode}</small></td>
            <td><b className={`order-status status-${order.status.toLowerCase()}`}>{order.status.replaceAll('_', ' ')}</b></td>
            <td><strong>{formatDateTime(order.createdAt)}</strong></td>
            <td><strong>{formatCurrency(order.amount)}</strong></td>
            {testControlsAvailable && <td>{canMockPay ? <button type="button" onClick={() => void onMockPaid(order)}>Mock paid</button> : <small>—</small>}</td>}
          </tr>;
        })}</tbody>
      </table>
    </div> : <div className="empty">{filtersActive ? 'No orders match these filters.' : 'No platform orders are currently retained.'}</div>}

    <div className="admin-pagination" aria-label="Order table pagination">
      <span>Showing {rangeStart}–{rangeEnd} of {filteredOrders.length}</span>
      <label>Rows per page
        <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
          <option value={10}>10</option>
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </label>
      <div>
        <button type="button" className="secondary" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
        <strong>Page {page} of {totalPages}</strong>
        <button type="button" className="secondary" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)}>Next</button>
      </div>
    </div>
  </div>;
}

function AdminShopsTable({ shops, overallTotal, filter, identityDeletionConfigured, onFilterChange, onToggleTest, onSubscription, onPrintingPause, onDelete }: {
  shops: ShopProfile[];
  overallTotal?: number;
  filter: AdminShopFilter;
  identityDeletionConfigured: boolean;
  onFilterChange: (filter: AdminShopFilter) => void;
  onToggleTest: (shop: ShopProfile, isTestAccount: boolean) => Promise<void>;
  onSubscription: (shop: ShopProfile, action: 'suspend' | 'activate') => Promise<void>;
  onPrintingPause: (shop: ShopProfile, paused: boolean) => Promise<void>;
  onDelete: (shop: ShopProfile) => Promise<void>;
}) {
  const [sortBy, setSortBy] = useState<AdminShopSort>('registered');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);

  const sortedShops = useMemo(() => [...shops].sort((left, right) => {
    let comparison = 0;
    if (sortBy === 'registered') {
      comparison = new Date(left.createdAt || 0).getTime() - new Date(right.createdAt || 0).getTime();
    } else if (sortBy === 'prints') {
      comparison = Number(left.printing?.jobs || 0) - Number(right.printing?.jobs || 0);
    } else {
      const leftValue = sortBy === 'name'
        ? left.shopName || left.shopId
        : sortBy === 'city'
          ? left.city || ''
          : left.onboardingComplete
            ? left.subscription?.status || left.subscriptionStatus || ''
            : 'Onboarding incomplete';
      const rightValue = sortBy === 'name'
        ? right.shopName || right.shopId
        : sortBy === 'city'
          ? right.city || ''
          : right.onboardingComplete
            ? right.subscription?.status || right.subscriptionStatus || ''
            : 'Onboarding incomplete';
      comparison = String(leftValue).localeCompare(String(rightValue), undefined, { numeric: true, sensitivity: 'base' });
    }
    return sortDirection === 'asc' ? comparison : -comparison;
  }), [shops, sortBy, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedShops.length / pageSize));
  const visibleShops = sortedShops.slice((page - 1) * pageSize, page * pageSize);
  const rangeStart = sortedShops.length ? (page - 1) * pageSize + 1 : 0;
  const rangeEnd = Math.min(page * pageSize, sortedShops.length);

  useEffect(() => setPage(1), [filter, sortBy, sortDirection, pageSize]);
  useEffect(() => setPage((current) => Math.min(current, totalPages)), [totalPages]);

  return (
    <div className="admin-shops-view">
      <div className="admin-shop-filters" aria-label="Shopkeeper filters">
        <button type="button" className={filter === 'production' ? 'primary' : 'secondary'} onClick={() => onFilterChange('production')}>Production</button>
        <button type="button" className={filter === 'incomplete' ? 'primary' : 'secondary'} onClick={() => onFilterChange('incomplete')}>Incomplete setup</button>
        <button type="button" className={filter === 'test' ? 'primary' : 'secondary'} onClick={() => onFilterChange('test')}>Test accounts</button>
        <button type="button" className={filter === 'all' ? 'primary' : 'secondary'} onClick={() => onFilterChange('all')}>All</button>
      </div>

      <div className="admin-shop-toolbar">
        <p className="admin-shop-count"><strong>{shops.length}</strong> shopkeeper{shops.length === 1 ? '' : 's'} in this view{typeof overallTotal === 'number' ? ` · ${overallTotal} total registered` : ''}</p>
        <div className="admin-shop-sort-controls">
          <label>Sort by
            <select value={sortBy} onChange={(event) => setSortBy(event.target.value as AdminShopSort)}>
              <option value="registered">Registration date</option>
              <option value="name">Shop name</option>
              <option value="city">City</option>
              <option value="status">Status</option>
              <option value="prints">Completed prints</option>
            </select>
          </label>
          <label>Order
            <select value={sortDirection} onChange={(event) => setSortDirection(event.target.value as 'asc' | 'desc')}>
              <option value="desc">{sortBy === 'registered' ? 'Newest first' : 'Descending'}</option>
              <option value="asc">{sortBy === 'registered' ? 'Oldest first' : 'Ascending'}</option>
            </select>
          </label>
        </div>
      </div>

      {visibleShops.length ? <div className="admin-table-wrap admin-shop-table-wrap">
        <table className="admin-table admin-shop-table">
          <thead><tr><th>Shopkeeper</th><th>Contact</th><th>Registered</th><th>Status</th><th>Printing</th><th>Agent</th><th>Actions</th></tr></thead>
          <tbody>{visibleShops.map((shop) => <AdminShopRow
            key={shop.shopId}
            shop={shop}
            identityDeletionConfigured={identityDeletionConfigured}
            onToggleTest={onToggleTest}
            onSubscription={onSubscription}
            onPrintingPause={onPrintingPause}
            onDelete={onDelete}
          />)}</tbody>
        </table>
      </div> : <div className="empty">No shopkeepers found in this view.</div>}

      <div className="admin-pagination" aria-label="Shopkeeper table pagination">
        <span>Showing {rangeStart}–{rangeEnd} of {sortedShops.length}</span>
        <label>Rows per page
          <select value={pageSize} onChange={(event) => setPageSize(Number(event.target.value))}>
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
          </select>
        </label>
        <div>
          <button type="button" className="secondary" disabled={page === 1} onClick={() => setPage((current) => current - 1)}>Previous</button>
          <strong>Page {page} of {totalPages}</strong>
          <button type="button" className="secondary" disabled={page === totalPages} onClick={() => setPage((current) => current + 1)}>Next</button>
        </div>
      </div>
    </div>
  );
}

function AdminShopRow({ shop, identityDeletionConfigured, onToggleTest, onSubscription, onPrintingPause, onDelete }: {
  shop: ShopProfile;
  identityDeletionConfigured: boolean;
  onToggleTest: (shop: ShopProfile, isTestAccount: boolean) => Promise<void>;
  onSubscription: (shop: ShopProfile, action: 'suspend' | 'activate') => Promise<void>;
  onPrintingPause: (shop: ShopProfile, paused: boolean) => Promise<void>;
  onDelete: (shop: ShopProfile) => Promise<void>;
}) {
  const verifiedAgent = Boolean(shop.agentConnectionVerifiedAt);
  const agentOnline = verifiedAgent && isAgentOnline(shop.agentLastSeenAt);
  const subscriptionStatus = shop.subscription?.status || shop.subscriptionStatus;
  const [deleting, setDeleting] = useState(false);
  const paymentReady = isShopPaymentReady(shop);
  const paymentDetails = shopPaymentDetails(shop);

  async function confirmDelete() {
    const name = shop.shopName || shop.shopId || 'this shop';
    const identityWarning = identityDeletionConfigured
      ? "the shopkeeper's Firebase sign-in account, "
      : '';
    const credentialWarning = identityDeletionConfigured
      ? ''
      : ' Firebase sign-in access will remain until Firebase Admin credentials are configured.';
    const confirmed = window.confirm(`Delete ${name}? This permanently deletes ${identityWarning}the shop profile, queued orders, and stored documents.${credentialWarning} Administrator audit records are retained.`);
    if (!confirmed) return;
    setDeleting(true);
    try {
      await onDelete(shop);
    } finally {
      setDeleting(false);
    }
  }

  return (
    <tr className={shop.onboardingComplete ? '' : 'onboarding-incomplete'}>
      <td><div className="shop-table-cell">
        <strong>{shop.shopName} {shop.isTestAccount && <span className="status-chip">TEST</span>}</strong>
        <small>{shop.shopId} · {shop.city || 'City not set'}</small>
        <small>{shop.address ? `${shop.address}${shop.pinCode ? ` – ${shop.pinCode}` : ''}` : 'Address not set'}</small>
      </div></td>
      <td><div className="shop-table-cell">
        <span>{shop.phoneNumber ? `📞 ${shop.phoneNumber}` : 'Contact number not set'}</span>
        <small>{shop.email ? `✉ ${shop.email}` : 'Email not set'}</small>
      </div></td>
      <td>{shop.createdAt ? <><strong>{formatDate(shop.createdAt)}</strong><small>{formatDateTime(shop.createdAt)}</small></> : '—'}</td>
      <td><div className="admin-card-facts shop-status-facts">
        <span>Free access</span>
        <b className={shop.onboardingComplete ? '' : 'needs-setup'}>{shop.onboardingComplete ? subscriptionStatus : 'Onboarding incomplete'}</b>
        <b className={shop.printingPaused ? 'needs-setup' : ''}>{shop.printingPaused ? 'Printing paused' : 'Printing active'}</b>
        <details className={`shop-payment-status${paymentReady ? ' ready' : ' required'}`}>
          <summary title="Show payment setup details">{paymentGatewayLabel(shop)}</summary>
          <div className="shop-payment-details">
            {paymentDetails.map((detail) => <span key={detail.label}><small>{detail.label}</small><strong>{detail.value}</strong></span>)}
            {!paymentDetails.length && <small>No payment provider details are available yet.</small>}
          </div>
        </details>
        <small>No subscription expiry</small>
      </div></td>
      <td><strong>{shop.printing?.jobs || 0} completed</strong><small>{formatCurrency(shop.printing?.value || 0)} value</small></td>
      <td><AgentConnectionEvidence shop={shop} online={agentOnline} verified={verifiedAgent} formatLastSeen={formatDateTime} /></td>
      <td><div className="shop-table-actions">
        <button type="button" onClick={() => void onToggleTest(shop, !shop.isTestAccount)}>{shop.isTestAccount ? 'Restore production' : 'Mark as test account'}</button>
        {subscriptionStatus === 'Suspended'
          ? <button type="button" onClick={() => void onSubscription(shop, 'activate')}>Activate</button>
          : <button type="button" className="danger" onClick={() => void onSubscription(shop, 'suspend')}>Suspend</button>}
        <button type="button" className={shop.printingPaused ? '' : 'danger'} onClick={() => void onPrintingPause(shop, !shop.printingPaused)}>{shop.printingPaused ? 'Resume printing' : 'Pause printing'}</button>
        <button type="button" className="danger" disabled={deleting} onClick={() => void confirmDelete()}>{deleting ? 'Deleting…' : 'Delete shop'}</button>
      </div></td>
    </tr>
  );
}

function OrderTable({ orders, canMockPay, onMockPay, now }: {
  orders: PortalOrder[];
  canMockPay: boolean;
  onMockPay: (order: PortalOrder) => void;
  now: number;
}) {
  if (!orders.length) return <div className="empty">No orders found.</div>;
  return (
    <div className="admin-table-wrap">
      <table className="admin-table">
        <thead><tr><th>Order</th><th>Document</th><th>Print</th><th>Amount</th><th>Status</th><th>Age</th><th>Action</th></tr></thead>
        <tbody>{orders.map((order) => (
          <tr key={`${order.shopId}-${order.orderId}`}>
            <td><strong>{order.orderId}</strong><small>{order.shopId}</small></td>
            <td>{order.fileName}</td>
            <td>{order.pageRange ? `${order.pageRange} (${order.pages}p)` : `${order.pages}p`} × {order.copies || 1}<small>{order.mode}</small></td>
            <td>₹{order.amount}</td>
            <td><span className={`order-status status-${order.status.toLowerCase()}`}>{order.status.replaceAll('_', ' ')}</span></td>
            <td><strong>{formatElapsed(order.createdAt, now)}</strong><small>{formatDateTime(order.createdAt)}</small></td>
            <td>{canMockPay && ['AWAITING_PAYMENT', 'PAYMENT_PENDING', 'PAYMENT_EXPIRED'].includes(order.status)
              ? <button type="button" onClick={() => onMockPay(order)}>Mock paid</button>
              : '—'}</td>
          </tr>
        ))}</tbody>
      </table>
    </div>
  );
}

function formatElapsed(value: string, now: number) {
  const startedAt = new Date(value).getTime();
  if (Number.isNaN(startedAt)) return '—';
  const minutes = Math.max(0, Math.floor((now - startedAt) / 60_000));
  return `${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}`;
}

function formatRemaining(milliseconds: number) {
  if (!Number.isFinite(milliseconds) || milliseconds <= 0) return 'ended';
  const totalMinutes = Math.ceil(milliseconds / 60_000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days) return `${days}d ${hours}h`;
  if (hours) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
}

function formatDate(value?: string) {
  if (!value) return 'Not set';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'Not set' : date.toLocaleDateString();
}

function formatDateTime(value?: string) {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
}

function isAgentOnline(lastSeenAt?: string) {
  if (!lastSeenAt) return false;
  const lastSeen = new Date(lastSeenAt).getTime();
  return Number.isFinite(lastSeen) && Date.now() - lastSeen <= 10 * 60 * 1000;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(Number(value || 0));
}

function salesAgentShareText(agent: SalesAgent) {
  return [
    'SaralPrint onboarding agent',
    `Name: ${agent.name}`,
    `Agent code: ${agent.code}`,
    `Phone: ${agent.phoneNumber}`,
    `City: ${agent.city}`,
    agent.email ? `Email: ${agent.email}` : ''
  ].filter(Boolean).join('\n');
}

function paymentGatewayLabel(shop: ShopProfile) {
  if (shop.checkoutMode === 'manual') return 'Manual payment · approve in agent';
  if (shop.checkoutMode === 'demo_pin') return 'Demo PIN active';
  if (shop.checkoutMode === 'counter_pin') return 'Counter PIN active';
  if (shop.checkoutMode === 'razorpay') return 'Razorpay via web';
  if (shop.checkoutMode === 'setup_required') return 'Payment setup required';
  if (shop.agentPaymentMode === 'manual') return 'Manual payment';
  if (shop.agentPaymentMode === 'gateway' && shop.agentPaymentProvider) {
    const provider = shop.agentPaymentProvider.charAt(0).toUpperCase() + shop.agentPaymentProvider.slice(1);
    return `${provider} via local agent`;
  }

  const provider = shop.paymentProvider
    ? shop.paymentProvider.charAt(0).toUpperCase() + shop.paymentProvider.slice(1)
    : '';
  if (shop.paymentConnectionStatus === 'connected' && provider) return `${provider} configured`;
  if (shop.paymentConnectionMode === 'platform') return 'Platform gateway available';
  if (shop.paymentConnectionMode === 'manual') return 'Payment mode not reported';
  return 'Gateway not configured';
}

function isShopPaymentReady(shop: ShopProfile) {
  return shop.paymentConfigured === true ||
    shop.paymentAvailable === true ||
    shop.checkoutMode === 'manual' ||
    shop.checkoutMode === 'razorpay' ||
    shop.checkoutMode === 'demo_pin' ||
    shop.checkoutMode === 'counter_pin' ||
    shop.agentPaymentMode === 'manual' ||
    shop.agentPaymentMode === 'gateway' ||
    shop.paymentConnectionStatus === 'connected';
}

function shopPaymentDetails(shop: ShopProfile) {
  const details: Array<{ label: string; value: string }> = [];
  const provider = shop.paymentProvider || shop.agentPaymentProvider;
  if (provider) details.push({ label: 'Provider', value: provider.charAt(0).toUpperCase() + provider.slice(1) });
  if (shop.razorpayAccountId) details.push({ label: 'Razorpay merchant ID', value: shop.razorpayAccountId });
  if (shop.paymentConnectionMode) {
    const connectionLabels: Record<NonNullable<ShopProfile['paymentConnectionMode']>, string> = {
      oauth: 'Razorpay OAuth',
      legacy_keys: 'Legacy API keys',
      platform: 'Platform gateway',
      manual: 'Manual / not connected',
    };
    details.push({ label: 'Connection', value: connectionLabels[shop.paymentConnectionMode] });
  }
  if (shop.checkoutMode && shop.checkoutMode !== 'setup_required') {
    const checkoutLabels: Record<Exclude<PaymentAccess['checkoutMode'], 'setup_required'>, string> = {
      manual: 'Manual payment · confirm each token in Android or Windows agent',
      razorpay: 'Razorpay web checkout',
      demo_pin: 'Demo payment PIN',
      counter_pin: 'Counter payment PIN',
    };
    details.push({ label: 'Payment mode', value: checkoutLabels[shop.checkoutMode] });
  } else if (shop.agentPaymentMode) {
    details.push({ label: 'Payment mode', value: shop.agentPaymentMode === 'gateway' ? 'Local agent gateway' : 'Manual payment' });
  }
  return details;
}


function formatAuthError(message: string) {
  if (!message) return '';
  if (message.includes('auth/operation-not-allowed')) {
    return 'Google sign-in is disabled in Firebase. Enable Authentication > Sign-in method > Google.';
  }
  if (message.includes('auth/unauthorized-domain')) {
    return `This domain is not authorized in Firebase: ${window.location.hostname}`;
  }
  return message.replace(/^Google sign-in failed\.?\s*/i, '');
}

const requestedLegalPage = legalPageAliases[window.location.pathname.toLowerCase().replace(/\/$/, '') || '/'];

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    {isTrainingPage ? <TrainingPage /> : requestedLegalPage ? <LegalPage page={requestedLegalPage} /> : <App />}
  </React.StrictMode>,
);
